/**
 * ProjectManagementCoursePro.jsx
 * OnboardLearning — Project Management Certificate
 * Features: Bilingual EN/FR · Score Tracker · PDF Certificate Export
 *
 * Usage:
 *   import ProjectManagementCoursePro from './ProjectManagementCoursePro';
 *   <ProjectManagementCoursePro />
 *
 * Dependencies: React only. jsPDF loaded via CDN at runtime.
 */

import { useState, useEffect, useCallback } from "react";
import { jsPDF } from "jspdf";

/* ═══════════════════════════════════════════════════════════
   UI TEXT (bilingual)
═══════════════════════════════════════════════════════════ */
const UI = {
  en: {
    logo: "OnboardLearning",
    subtitle: "Project Management Certificate",
    progress: "Course Progress",
    prevBtn: "← Previous",
    nextBtn: "Next →",
    finishBtn: "Finish",
    keyConcepts: "Key Concepts",
    quizTitle: "Knowledge Check",
    quizSub: "Test your understanding of this module",
    checkBtn: "Check Answer",
    correct: "✅ Correct! ",
    wrong: "❌ Not quite. ",
    markComplete: "Mark Complete & Continue →",
    moduleOf: (c, t) => `Module ${c} of ${t}`,
    breadcrumb: "Project Management",
    scorePanel: "Score Tracker",
    scoreClose: "Close",
    scoreMod: "Module",
    scoreResult: "Result",
    scorePoints: "Points",
    scoreTotal: "Total Score",
    scoreNotDone: "Not attempted",
    scoreCorrect: "Correct",
    scoreWrong: "Incorrect",
    certBtn: "🏆 Download Certificate",
    certModal: "Your Certificate",
    certNameLabel: "Enter your full name",
    certNamePlaceholder: "e.g. John Smith",
    certGenerate: "Generate & Download PDF",
    certClose: "Cancel",
    certLang: "Certificate Language",
    certScoreLabel: "Final Score",
    allDoneTitle: "Course Complete!",
    allDoneMsg: "You've completed all 10 modules of the Project Management Certificate.",
    groups: [
      "Foundations & Initiation",
      "Planning & Scheduling",
      "Execution & Control",
      "Communication & Quality",
      "Project Closure",
    ],
    langToggle: "🇫🇷 Français",
  },
  fr: {
    logo: "OnboardLearning",
    subtitle: "Certificat en Gestion de Projet",
    progress: "Progression du cours",
    prevBtn: "← Précédent",
    nextBtn: "Suivant →",
    finishBtn: "Terminer",
    keyConcepts: "Concepts Clés",
    quizTitle: "Vérification des Connaissances",
    quizSub: "Testez votre compréhension de ce module",
    checkBtn: "Vérifier la réponse",
    correct: "✅ Correct ! ",
    wrong: "❌ Pas tout à fait. ",
    markComplete: "Marquer comme terminé & Continuer →",
    moduleOf: (c, t) => `Module ${c} sur ${t}`,
    breadcrumb: "Gestion de Projet",
    scorePanel: "Suivi des Scores",
    scoreClose: "Fermer",
    scoreMod: "Module",
    scoreResult: "Résultat",
    scorePoints: "Points",
    scoreTotal: "Score Total",
    scoreNotDone: "Non tenté",
    scoreCorrect: "Correct",
    scoreWrong: "Incorrect",
    certBtn: "🏆 Télécharger le Certificat",
    certModal: "Votre Certificat",
    certNameLabel: "Entrez votre nom complet",
    certNamePlaceholder: "ex. Jean Dupont",
    certGenerate: "Générer & Télécharger le PDF",
    certClose: "Annuler",
    certLang: "Langue du certificat",
    certScoreLabel: "Score Final",
    allDoneTitle: "Cours Terminé !",
    allDoneMsg: "Vous avez complété les 10 modules du Certificat en Gestion de Projet.",
    groups: [
      "Fondements & Initiation",
      "Planification & Calendrier",
      "Exécution & Contrôle",
      "Communication & Qualité",
      "Clôture de Projet",
    ],
    langToggle: "🇬🇧 English",
  },
};

/* ═══════════════════════════════════════════════════════════
   MODULES — ENGLISH
═══════════════════════════════════════════════════════════ */
const MODULES_EN = [
  {
    id:1, tag:"Foundations", title:"Introduction to Project Management",
    duration:"15 min", level:"Beginner",
    intro:"Project management is the discipline of planning, organizing, and overseeing the completion of a project while ensuring it meets goals within time, budget, and quality constraints. This module lays the foundation for your entire learning journey.",
    steps:[
      { name:"What is a Project?", desc:"Definition, characteristics, and examples",
        content:`A <strong>project</strong> is a temporary endeavor undertaken to create a unique product, service, or result. Unlike ongoing operations, projects have a defined beginning and end.<br/><br/>Three characteristics define every project:<ul><li><strong>Temporary</strong> — every project has a start and finish date</li><li><strong>Unique</strong> — the deliverable is distinct from any prior work</li><li><strong>Progressive elaboration</strong> — details are developed incrementally</li></ul><div class="pm-callout"><strong>Real-world example</strong><p>Building a new school campus in Bangui: fixed budget, defined timeline, unique architecture — that's a project. Maintaining it afterward is an operation.</p></div>`},
      { name:"The Triple Constraint", desc:"Scope, Time & Cost — the iron triangle",
        content:`Every project lives within three competing constraints:<ul><li><strong>Scope</strong> — what work must be done</li><li><strong>Time</strong> — when it must be done by</li><li><strong>Cost</strong> — how much can be spent</li></ul><p>Quality sits at the center. Changing one constraint affects the others — this is why scope creep is so dangerous.</p><div class="pm-callout"><strong>Rule of thumb</strong><p>Fast + Cheap = Low Quality. Good + Fast = Expensive. Good + Cheap = Slow. You can optimize two — never all three.</p></div>`},
      { name:"Project vs. Operations", desc:"Understanding the key difference",
        content:`Operations are <strong>ongoing, repetitive work</strong> that keeps the business running. Projects are <strong>one-time efforts</strong> that create something new.<ul><li>Operations: payroll, customer support, manufacturing</li><li>Projects: launching an app, building a warehouse, a marketing campaign</li></ul>`},
      { name:"Types of Project Management", desc:"Waterfall, Agile, Hybrid",
        content:`Three dominant methodologies:<ul><li><strong>Waterfall</strong> — sequential phases; best for construction or fixed-scope work</li><li><strong>Agile</strong> — iterative sprints; best for software and fast-changing requirements</li><li><strong>Hybrid</strong> — combines both; great for complex, multi-team projects</li></ul>`},
    ],
    concepts:[
      {icon:"📋",title:"Project Charter",desc:"Formal authorization document for a project"},
      {icon:"⚖️",title:"Triple Constraint",desc:"Scope, time, and cost in balance"},
      {icon:"🎯",title:"Deliverable",desc:"Tangible output produced by project work"},
      {icon:"👤",title:"Project Manager",desc:"Person responsible for achieving project objectives"},
    ],
    quiz:{ q:"Which BEST describes the triple constraint?",
      options:["Scope, Quality, and Risk","Scope, Time, and Cost","Budget, Team, and Timeline","Planning, Execution, and Closure"],
      answer:1, explanation:"The Triple Constraint (Iron Triangle) consists of Scope, Time, and Cost. Quality sits at the center and is affected by changes to any of these three."},
  },
  {
    id:2, tag:"Initiation", title:"Project Initiation & Charter",
    duration:"20 min", level:"Beginner",
    intro:"Initiation is the first phase of any project. This is where you define what the project is, why it matters, who's involved, and whether it's feasible. A strong initiation prevents costly mistakes later.",
    steps:[
      { name:"Identifying Stakeholders", desc:"Who matters and why",
        content:`A <strong>stakeholder</strong> is anyone who can affect or be affected by the project.<ul><li><strong>Internal</strong>: executives, team members, finance dept.</li><li><strong>External</strong>: clients, vendors, government, communities</li></ul><div class="pm-callout"><strong>Key insight</strong><p>A stakeholder ignored during initiation can become a project killer during execution. Engagement early costs less than conflict late.</p></div>`},
      { name:"Conducting a Feasibility Study", desc:"Is the project worth doing?",
        content:`Assess feasibility across four dimensions:<ul><li><strong>Technical</strong> — Do we have the skills and tools?</li><li><strong>Financial</strong> — Is the ROI positive?</li><li><strong>Operational</strong> — Can we absorb the change?</li><li><strong>Legal/Regulatory</strong> — Are there compliance requirements?</li></ul>`},
      { name:"Writing the Project Charter", desc:"The birth certificate of your project",
        content:`The <strong>Project Charter</strong> officially authorizes the project and gives the PM authority to apply resources. It must include:<ul><li>Project purpose and objectives</li><li>High-level scope and deliverables</li><li>Budget and timeline estimates</li><li>Key stakeholders and roles</li><li>Success criteria and KPIs</li><li>Assumptions, constraints, and risks</li></ul>`},
      { name:"Defining Success Criteria", desc:"How will you know you've won?",
        content:`Success criteria must be <strong>SMART</strong>: Specific, Measurable, Achievable, Relevant, Time-bound.<br/>Example: "Deploy the LMS to 50 schools with 99.5% uptime by Q4 2026" beats "build a learning platform."`},
    ],
    concepts:[
      {icon:"📜",title:"Project Charter",desc:"Formal document authorizing the project"},
      {icon:"🔍",title:"Feasibility Study",desc:"Analysis of project viability"},
      {icon:"👥",title:"Stakeholder Register",desc:"Catalog of all project stakeholders"},
      {icon:"✅",title:"SMART Goals",desc:"Criteria for well-defined objectives"},
    ],
    quiz:{ q:"What is the PRIMARY purpose of a Project Charter?",
      options:["To assign daily tasks to the team","To formally authorize the project and give the PM authority","To create the project schedule","To document lessons learned"],
      answer:1, explanation:"The Project Charter officially authorizes the project and grants the PM authority to use organizational resources. It's the foundational governance document."},
  },
  {
    id:3, tag:"Planning", title:"Project Planning Fundamentals",
    duration:"25 min", level:"Intermediate",
    intro:"Planning is where most project success is won or lost. A thorough project plan acts as your roadmap — aligning the team, setting expectations, and providing a baseline to measure against throughout execution.",
    steps:[
      { name:"The Project Management Plan", desc:"Your master blueprint",
        content:`The <strong>Project Management Plan</strong> (PMP) integrates all subsidiary plans:<ul><li>Scope Management Plan</li><li>Schedule Management Plan</li><li>Cost Management Plan</li><li>Quality Management Plan</li><li>Risk Management Plan</li><li>Communication Plan</li></ul><p>The PMP is a living document — updated as the project evolves.</p>`},
      { name:"Defining Scope", desc:"What's in and what's out",
        content:`<strong>Scope</strong> defines the sum of all work required:<ul><li><strong>Scope statement</strong> — detailed description of deliverables</li><li><strong>Inclusions</strong> — explicitly what IS included</li><li><strong>Exclusions</strong> — explicitly what is NOT included</li><li><strong>Acceptance criteria</strong> — conditions that must be met</li></ul><div class="pm-callout"><strong>Scope creep warning</strong><p>Every undocumented "small addition" erodes budget and timeline. Document everything. Change = formal change request.</p></div>`},
      { name:"Creating a WBS", desc:"Work Breakdown Structure — divide and conquer",
        content:`A <strong>Work Breakdown Structure</strong> breaks project scope into manageable work packages.<ul><li>Level 1: Project</li><li>Level 2: Major deliverables / phases</li><li>Level 3: Work packages (assignable, estimable)</li></ul><p>The <strong>100% Rule</strong>: WBS must capture 100% of the project work.</p>`},
      { name:"Estimating Time & Cost", desc:"Realistic baselines for control",
        content:`Common estimation techniques:<ul><li><strong>Analogous</strong> — use data from similar past projects</li><li><strong>Parametric</strong> — statistical models</li><li><strong>Three-point</strong> — (Optimistic + Pessimistic + 4×Most Likely) ÷ 6</li><li><strong>Bottom-up</strong> — estimate each task and roll up</li></ul><p>Always build in <strong>contingency reserve</strong> (known risks) and <strong>management reserve</strong> (unknown risks).</p>`},
    ],
    concepts:[
      {icon:"🗺️",title:"Project Management Plan",desc:"Master document integrating all plans"},
      {icon:"📐",title:"WBS",desc:"Hierarchical breakdown of all project work"},
      {icon:"⏱️",title:"Estimation",desc:"Predicting cost and duration of work"},
      {icon:"🚫",title:"Scope Exclusions",desc:"What is explicitly NOT part of the project"},
    ],
    quiz:{ q:"What does the '100% Rule' mean in the context of a Work Breakdown Structure?",
      options:["All team members must work 100% of the time","The WBS must capture 100% of all project work","The budget must be 100% allocated before work begins","All tasks must be 100% complete before project closure"],
      answer:1, explanation:"The 100% Rule means the WBS must include 100% of the work defined in the project scope — not more, not less. Nothing should be left out or double-counted."},
  },
  {
    id:4, tag:"Scheduling", title:"Project Scheduling & Time Management",
    duration:"25 min", level:"Intermediate",
    intro:"A schedule transforms your plan into a timeline. It sequences tasks, assigns resources, and identifies the critical path that determines when your project will finish.",
    steps:[
      { name:"Activity Sequencing", desc:"Dependencies define your flow",
        content:`Four types of task dependencies:<ul><li><strong>Finish-to-Start (FS)</strong> — B can't start until A finishes (most common)</li><li><strong>Start-to-Start (SS)</strong> — Both tasks start simultaneously</li><li><strong>Finish-to-Finish (FF)</strong> — Both tasks must finish together</li><li><strong>Start-to-Finish (SF)</strong> — Rare; B can't finish until A starts</li></ul>`},
      { name:"The Critical Path Method", desc:"CPM — your project's spine",
        content:`The <strong>Critical Path</strong> is the longest sequence of dependent tasks and determines the minimum project duration.<ul><li>Any delay on the critical path delays the whole project</li><li>Critical-path tasks have <strong>zero float</strong></li><li>Non-critical tasks have <strong>slack</strong> — they can slip without impacting the end date</li></ul><div class="pm-callout"><strong>PM tip</strong><p>Focus your monitoring energy on critical-path tasks. Non-critical tasks with large float can wait — critical tasks cannot.</p></div>`},
      { name:"Gantt Charts", desc:"Visualizing the schedule",
        content:`A <strong>Gantt chart</strong> displays tasks on a horizontal timeline with bars showing start, duration, and end.<ul><li>Shows task dependencies visually</li><li>Easy to communicate with stakeholders</li><li>Highlights milestones and deadlines</li></ul><p>Popular tools: MS Project, Asana, Monday.com, Smartsheet.</p>`},
      { name:"Schedule Compression", desc:"When you need to go faster",
        content:`Two techniques:<ul><li><strong>Crashing</strong> — Add resources to critical-path tasks. Cost increases.</li><li><strong>Fast Tracking</strong> — Run tasks in parallel that were originally sequential. Risk increases.</li></ul><p>Always document trade-offs before compressing.</p>`},
    ],
    concepts:[
      {icon:"🛤️",title:"Critical Path",desc:"Longest path of dependent tasks in the project"},
      {icon:"📊",title:"Gantt Chart",desc:"Visual bar chart of project schedule"},
      {icon:"⏳",title:"Float / Slack",desc:"Time a task can be delayed without impacting the deadline"},
      {icon:"🔗",title:"Dependencies",desc:"Relationships that determine task order"},
    ],
    quiz:{ q:"What does 'zero float' on a task mean?",
      options:["The task has no assigned team member","The task has no cost","The task is on the critical path and cannot be delayed","The task is optional"],
      answer:2, explanation:"A task with zero float is on the critical path — any delay to it directly delays the project end date. These tasks require the most vigilant monitoring."},
  },
  {
    id:5, tag:"Resources", title:"Resource Management",
    duration:"20 min", level:"Intermediate",
    intro:"People are your most valuable project resource. Resource management covers how you acquire, develop, manage, and optimize your team — as well as physical and financial resources.",
    steps:[
      { name:"Resource Planning", desc:"What and who do you need?",
        content:`Resource planning answers three questions:<ul><li><strong>What</strong> resources are needed? (people, equipment, materials)</li><li><strong>How many</strong> are needed?</li><li><strong>When</strong> are they needed?</li></ul><p>Build a <strong>Resource Breakdown Structure (RBS)</strong> that mirrors your WBS.</p>`},
      { name:"Building Your Team", desc:"Roles, responsibilities & RACI",
        content:`A <strong>RACI matrix</strong> clarifies accountability:<ul><li><strong>R</strong> — Responsible: does the work</li><li><strong>A</strong> — Accountable: owns the outcome (one per task)</li><li><strong>C</strong> — Consulted: provides input</li><li><strong>I</strong> — Informed: kept up to date</li></ul><div class="pm-callout"><strong>Rule</strong><p>Every task must have exactly ONE person Accountable. Multiple Accountable = no one accountable.</p></div>`},
      { name:"Resource Leveling", desc:"Eliminating over-allocation",
        content:`Resource leveling resolves conflicts when team members are over-allocated:<ul><li>Delay non-critical tasks until resources free up</li><li>Split tasks across multiple periods</li><li>Hire or contract additional resources</li></ul>`},
      { name:"Motivation & Team Development", desc:"Tuckman's model",
        content:`Tuckman's stages of team development:<ul><li><strong>Forming</strong> — polite, unclear roles</li><li><strong>Storming</strong> — conflict as personalities clash</li><li><strong>Norming</strong> — rules emerge, trust builds</li><li><strong>Performing</strong> — high output, autonomous</li><li><strong>Adjourning</strong> — project ends, team disperses</li></ul>`},
    ],
    concepts:[
      {icon:"👷",title:"Resource Breakdown",desc:"Hierarchical list of all project resources"},
      {icon:"📋",title:"RACI Matrix",desc:"Responsibility assignment framework"},
      {icon:"⚖️",title:"Resource Leveling",desc:"Resolving over-allocation conflicts"},
      {icon:"🔄",title:"Tuckman's Model",desc:"Stages of team development"},
    ],
    quiz:{ q:"In a RACI matrix, which role should have exactly ONE person assigned per task?",
      options:["Responsible","Accountable","Consulted","Informed"],
      answer:1, explanation:"Each task should have exactly ONE Accountable person. This person owns the outcome. Multiple accountable parties creates confusion and diffused responsibility."},
  },
  {
    id:6, tag:"Risk", title:"Risk Management",
    duration:"25 min", level:"Intermediate",
    intro:"Every project faces uncertainty. Risk management is the systematic process of identifying, analyzing, and responding to project risks — both threats and opportunities — to protect project outcomes.",
    steps:[
      { name:"Risk Identification", desc:"You can't manage what you don't know",
        content:`Risk identification should be a team exercise. Common techniques:<ul><li><strong>Brainstorming</strong> — open, judgment-free risk generation</li><li><strong>SWOT Analysis</strong> — Strengths, Weaknesses, Opportunities, Threats</li><li><strong>Expert interviews</strong> — tap domain experience</li><li><strong>Checklist</strong> — industry-specific risk libraries</li></ul><p>Document every risk in a <strong>Risk Register</strong>.</p>`},
      { name:"Qualitative Risk Analysis", desc:"Prioritize by probability and impact",
        content:`Rate each risk on two scales:<ul><li><strong>Probability</strong>: Very Low (5%) → Very High (90%)</li><li><strong>Impact</strong>: Negligible → Catastrophic</li></ul><p>Plot on a <strong>Probability-Impact Matrix</strong>.</p><div class="pm-callout"><strong>Priority zones</strong><p>Red = respond immediately. Yellow = watch and plan. Green = accept or monitor passively.</p></div>`},
      { name:"Risk Response Strategies", desc:"Four ways to deal with threats",
        content:`For threats (negative risks):<ul><li><strong>Avoid</strong> — eliminate the risk entirely</li><li><strong>Transfer</strong> — shift impact to a third party (insurance)</li><li><strong>Mitigate</strong> — reduce probability or impact</li><li><strong>Accept</strong> — acknowledge and budget for it</li></ul><p>For opportunities: Exploit, Share, Enhance, or Accept.</p>`},
      { name:"Risk Monitoring & Control", desc:"Risks don't manage themselves",
        content:`Risk management is ongoing throughout the project:<ul><li>Review the Risk Register at every status meeting</li><li>Update probability/impact ratings as conditions change</li><li>Identify new risks as work progresses</li><li>Execute contingency plans when trigger conditions are met</li></ul>`},
    ],
    concepts:[
      {icon:"📊",title:"Risk Register",desc:"Living document tracking all identified risks"},
      {icon:"🎲",title:"P-I Matrix",desc:"Probability-Impact grid for risk prioritization"},
      {icon:"🛡️",title:"Risk Response",desc:"Strategy for addressing each risk"},
      {icon:"🔔",title:"Risk Triggers",desc:"Indicators that a risk event has occurred"},
    ],
    quiz:{ q:"Which risk response strategy involves shifting the financial impact of a risk to a third party?",
      options:["Avoid","Mitigate","Transfer","Accept"],
      answer:2, explanation:"Transfer moves the financial consequence of a risk to another party — typically through insurance, bonds, or fixed-price contracts. The risk exists but the organization is protected from bearing the cost."},
  },
  {
    id:7, tag:"Execution", title:"Project Execution & Control",
    duration:"25 min", level:"Advanced",
    intro:"Execution is where plans meet reality. This phase involves directing and managing project work, while controlling ensures performance stays aligned with the plan. Most of a PM's time is spent here.",
    steps:[
      { name:"Directing & Managing Work", desc:"From plan to action",
        content:`During execution, the PM's key responsibilities:<ul><li>Assign work packages to team members</li><li>Remove obstacles (being the team's shield)</li><li>Manage vendor and contractor performance</li><li>Implement approved changes</li><li>Collect work performance data continuously</li></ul>`},
      { name:"Earned Value Management", desc:"The gold standard of project control",
        content:`<strong>EVM</strong> uses three metrics:<ul><li><strong>PV</strong> — Planned Value: budgeted cost of scheduled work</li><li><strong>EV</strong> — Earned Value: budgeted cost of work completed</li><li><strong>AC</strong> — Actual Cost: actual cost incurred</li></ul><p><strong>SV = EV − PV</strong> | <strong>CV = EV − AC</strong> | <strong>CPI = EV ÷ AC</strong></p><div class="pm-callout"><strong>Key rule</strong><p>CPI below 1.0 = over budget. SPI below 1.0 = behind schedule. Both negative = act immediately.</p></div>`},
      { name:"Change Control Process", desc:"Managing the inevitable",
        content:`Every change must go through formal <strong>Integrated Change Control</strong>:<ul><li>Change request submitted in writing</li><li>Impact assessed (scope, time, cost, risk)</li><li>Change Control Board (CCB) approves or rejects</li><li>Approved changes update the PMP and baselines</li><li>Team is notified of all approved changes</li></ul>`},
      { name:"Status Reporting", desc:"Keeping stakeholders aligned",
        content:`A good status report includes:<ul><li>Summary of work completed this period</li><li>Key metrics: schedule, cost, scope (Red/Yellow/Green)</li><li>Risks and issues requiring decisions</li><li>Upcoming work next period</li><li>Change requests pending</li></ul>`},
    ],
    concepts:[
      {icon:"📈",title:"Earned Value",desc:"Measure of work completed vs. planned"},
      {icon:"🔄",title:"Change Control",desc:"Formal process for approving changes"},
      {icon:"🚦",title:"RAG Status",desc:"Red/Amber/Green health indicators"},
      {icon:"📉",title:"Variance Analysis",desc:"Comparing actuals to baseline"},
    ],
    quiz:{ q:"If CPI = 0.85 on your project, what does this indicate?",
      options:["The project is 85% complete","For every $1 spent, you are getting $0.85 of planned value","The project has 85% stakeholder approval","15% of work has been rescheduled"],
      answer:1, explanation:"CPI = EV ÷ AC. A CPI of 0.85 means for every $1 actually spent, only $0.85 of planned work has been accomplished — the project is over budget."},
  },
  {
    id:8, tag:"Communication", title:"Communication Management",
    duration:"15 min", level:"Intermediate",
    intro:"Studies show that project managers spend up to 90% of their time communicating. Effective communication management ensures the right information reaches the right people at the right time in the right format.",
    steps:[
      { name:"Communication Planning", desc:"Who needs to know what, and when?",
        content:`A <strong>Communication Management Plan</strong> documents:<ul><li>Who needs information (audience)</li><li>What information they need</li><li>When and how often to communicate</li><li>Which channels to use (email, meetings, dashboards)</li><li>Who is responsible for each communication</li></ul><p>Formula: <strong>N(N−1)/2</strong> = number of communication channels. With 10 people = 45 channels.</p>`},
      { name:"Meeting Management", desc:"Make every meeting count",
        content:`Effective meetings have:<ul><li>A clear agenda sent 24 hours in advance</li><li>A defined purpose (decision, status, problem-solving)</li><li>Only essential attendees</li><li>Minutes with action items distributed within 24 hours</li></ul><div class="pm-callout"><strong>PM axiom</strong><p>If you don't have an agenda, don't have the meeting.</p></div>`},
      { name:"Conflict Resolution", desc:"Conflict is inevitable — dysfunction is not",
        content:`Thomas-Kilmann conflict modes:<ul><li><strong>Collaborate</strong> — win-win; best long-term (PMI recommended)</li><li><strong>Compromise</strong> — partial win for both; good for speed</li><li><strong>Accommodate</strong> — yield to the other; use rarely</li><li><strong>Force</strong> — fastest but damages relationships</li><li><strong>Avoid</strong> — only for trivial issues</li></ul>`},
      { name:"Stakeholder Engagement", desc:"Turning resistance into support",
        content:`Stakeholders move through engagement levels:<br/><strong>Unaware → Resistant → Neutral → Supportive → Champion</strong><br/><br/>Your goal: move each stakeholder to at least <strong>Supportive</strong>. Strategies: 1-on-1 briefings, early involvement, transparent reporting, addressing concerns proactively.`},
    ],
    concepts:[
      {icon:"📡",title:"Communication Plan",desc:"Who, what, when, how for each stakeholder"},
      {icon:"🤝",title:"Conflict Resolution",desc:"Structured approaches to resolving disagreements"},
      {icon:"📣",title:"Stakeholder Engagement",desc:"Moving stakeholders from resistant to champion"},
      {icon:"📝",title:"Meeting Minutes",desc:"Action-item record from project meetings"},
    ],
    quiz:{ q:"What formula calculates the number of communication channels in a project team?",
      options:["N × 2","N(N−1)/2","N² − N","N + (N−1)"],
      answer:1, explanation:"The formula N(N−1)/2 gives the total number of communication channels. With 5 people = 10 channels; with 10 people = 45. Complexity grows fast as teams expand."},
  },
  {
    id:9, tag:"Quality", title:"Quality Management",
    duration:"20 min", level:"Intermediate",
    intro:"Quality management ensures that the project satisfies the needs for which it was undertaken. It covers both project quality (how the work is done) and product quality (what is delivered).",
    steps:[
      { name:"Quality Planning", desc:"Define quality before you measure it",
        content:`Quality planning identifies which standards are relevant and how to satisfy them. Outputs:<ul><li><strong>Quality Management Plan</strong> — policies, processes, and roles</li><li><strong>Quality Metrics</strong> — defect rate, uptime %, satisfaction score</li><li><strong>Checklists</strong> — task-level quality gates</li></ul><p>Cost of quality = prevention + appraisal costs (which reduce expensive failure costs).</p>`},
      { name:"Quality Assurance", desc:"Are we following the right processes?",
        content:`<strong>Quality Assurance (QA)</strong> is process-oriented and proactive — it audits the processes used to produce deliverables to ensure they are appropriate.<ul><li>Process audits</li><li>Root cause analysis</li><li>Continuous process improvement</li></ul><p>QA prevents defects from occurring.</p>`},
      { name:"Quality Control", desc:"Are our deliverables meeting standards?",
        content:`<strong>Quality Control (QC)</strong> is product-oriented and reactive — it inspects actual deliverables against defined standards.<ul><li>Testing and inspection</li><li>Defect tracking and reporting</li><li>Control charts and fishbone diagrams</li></ul><div class="pm-callout"><strong>Key distinction</strong><p>QA = doing the right things. QC = checking things were done right. You need both.</p></div>`},
      { name:"Continuous Improvement", desc:"Never stop getting better",
        content:`Quality frameworks:<ul><li><strong>Kaizen</strong> — small, incremental daily improvements</li><li><strong>PDCA Cycle</strong> — Plan, Do, Check, Act (Deming Cycle)</li><li><strong>Six Sigma</strong> — data-driven reduction of defects</li><li><strong>Lean</strong> — eliminate waste and maximize value</li></ul>`},
    ],
    concepts:[
      {icon:"✅",title:"Quality Metrics",desc:"Specific measurements for product/process quality"},
      {icon:"🔍",title:"Quality Assurance",desc:"Auditing processes to prevent defects"},
      {icon:"🧪",title:"Quality Control",desc:"Inspecting outputs to catch defects"},
      {icon:"🔄",title:"PDCA Cycle",desc:"Plan-Do-Check-Act continuous improvement loop"},
    ],
    quiz:{ q:"What is the key difference between Quality Assurance and Quality Control?",
      options:["QA is done by engineers; QC is done by managers","QA audits processes to prevent defects; QC inspects outputs to find defects","QA happens at end of project; QC happens at the start","There is no meaningful difference"],
      answer:1, explanation:"QA is process-focused and proactive — it prevents defects. QC is product-focused and reactive — it finds and fixes defects. Both are essential."},
  },
  {
    id:10, tag:"Closure", title:"Project Closure & Lessons Learned",
    duration:"15 min", level:"All Levels",
    intro:"Project closure is often the most neglected phase — yet it's where organizations harvest the value of their investment. Proper closure protects the team, satisfies stakeholders, and seeds future projects with institutional knowledge.",
    steps:[
      { name:"Formal Acceptance", desc:"Getting the client's signature",
        content:`Formal acceptance confirms all deliverables meet agreed acceptance criteria.<ul><li>Walk the client through all deliverables</li><li>Obtain written sign-off on each</li><li>Document any outstanding items and agree on resolution timelines</li><li>Transfer ownership of assets, documentation, and credentials</li></ul>`},
      { name:"Closing Contracts", desc:"Tying up every legal thread",
        content:`All vendor and contractor contracts must be formally closed:<ul><li>Verify all contracted deliverables were received</li><li>Confirm final invoices are paid</li><li>Release resources per contract terms</li><li>Document vendor performance for future reference</li><li>Obtain warranties, guarantees, or support agreements</li></ul>`},
      { name:"Lessons Learned", desc:"The most valuable output of any project",
        content:`A structured lessons-learned session covers:<ul><li>What was planned vs. what actually happened?</li><li>What were the root causes of key variances?</li><li>What should we start, stop, or continue doing?</li><li>What knowledge should be preserved for future PMs?</li></ul><div class="pm-callout"><strong>Critical insight</strong><p>Organizations that skip lessons learned are doomed to repeat expensive mistakes. Archive them — future PMs depend on it.</p></div>`},
      { name:"Team Recognition & Release", desc:"Ending on a high note",
        content:`The final PM responsibility is to the team:<ul><li>Formally recognize and celebrate contributions</li><li>Write performance evaluations and recommendations</li><li>Help team members transition to their next assignments</li><li>Archive all project documentation per retention policies</li></ul>`},
    ],
    concepts:[
      {icon:"✍️",title:"Formal Acceptance",desc:"Written sign-off on all project deliverables"},
      {icon:"📚",title:"Lessons Learned",desc:"Documented knowledge for future projects"},
      {icon:"🤝",title:"Contract Closure",desc:"Formal close-out of vendor agreements"},
      {icon:"🏆",title:"Team Recognition",desc:"Celebrating contributions and releasing resources"},
    ],
    quiz:{ q:"Why is formal written acceptance of deliverables critical at project closure?",
      options:["It triggers the final payment to the PM","It confirms deliverables meet criteria and protects against future liability claims","It is only required for government contracts","It allows the team to start the next project"],
      answer:1, explanation:"Formal written acceptance confirms all deliverables meet agreed acceptance criteria. Without it, the performing organization may remain liable for ongoing issues after the project ends."},
  },
];

/* ═══════════════════════════════════════════════════════════
   MODULES — FRENCH
═══════════════════════════════════════════════════════════ */
const MODULES_FR = [
  {
    id:1, tag:"Fondements", title:"Introduction à la Gestion de Projet",
    duration:"15 min", level:"Débutant",
    intro:"La gestion de projet est la discipline qui consiste à planifier, organiser et superviser la réalisation d'un projet tout en s'assurant qu'il atteigne ses objectifs dans les délais, le budget et les contraintes de qualité. Ce module pose les bases de tout votre parcours d'apprentissage.",
    steps:[
      { name:"Qu'est-ce qu'un Projet ?", desc:"Définition, caractéristiques et exemples",
        content:`Un <strong>projet</strong> est un effort temporaire entrepris pour créer un produit, un service ou un résultat unique. Contrairement aux opérations courantes, les projets ont un début et une fin définis.<br/><br/>Trois caractéristiques définissent tout projet :<ul><li><strong>Temporaire</strong> — tout projet a une date de début et de fin</li><li><strong>Unique</strong> — le livrable se distingue de tout travail antérieur</li><li><strong>Élaboration progressive</strong> — les détails sont développés de manière incrémentale</li></ul><div class="pm-callout"><strong>Exemple concret</strong><p>La construction d'un nouveau campus scolaire à Bangui : budget fixe, calendrier défini, architecture unique — c'est un projet. L'entretien du campus ensuite est une opération.</p></div>`},
      { name:"La Triple Contrainte", desc:"Périmètre, Délai & Coût — le triangle de fer",
        content:`Tout projet vit dans trois contraintes concurrentes :<ul><li><strong>Périmètre</strong> — quel travail doit être réalisé</li><li><strong>Délai</strong> — quand cela doit être fait</li><li><strong>Coût</strong> — combien peut être dépensé</li></ul><p>La qualité se situe au centre de ce triangle. Modifier une contrainte affecte les autres — c'est pourquoi le glissement de périmètre est si dangereux.</p><div class="pm-callout"><strong>Règle pratique</strong><p>Rapide + Pas Cher = Basse Qualité. Bien + Rapide = Cher. Bien + Pas Cher = Lent. On peut optimiser deux — jamais les trois.</p></div>`},
      { name:"Projet vs. Opérations", desc:"Comprendre la différence fondamentale",
        content:`Les opérations sont un <strong>travail continu et répétitif</strong> qui maintient l'entreprise en fonctionnement. Les projets sont des <strong>efforts ponctuels</strong> qui créent quelque chose de nouveau.<ul><li>Opérations : paie, support client, fabrication</li><li>Projets : lancer une application, construire un entrepôt, une campagne marketing</li></ul>`},
      { name:"Types de Gestion de Projet", desc:"Cascade, Agile, Hybride",
        content:`Trois méthodologies dominantes :<ul><li><strong>Cascade (Waterfall)</strong> — phases séquentielles ; idéale pour la construction ou le périmètre fixe</li><li><strong>Agile</strong> — sprints itératifs ; idéale pour les logiciels et les exigences évolutives</li><li><strong>Hybride</strong> — combine les deux ; parfaite pour les projets complexes multi-équipes</li></ul>`},
    ],
    concepts:[
      {icon:"📋",title:"Charte de Projet",desc:"Document d'autorisation formelle du projet"},
      {icon:"⚖️",title:"Triple Contrainte",desc:"Périmètre, délai et coût en équilibre"},
      {icon:"🎯",title:"Livrable",desc:"Résultat tangible produit par le travail du projet"},
      {icon:"👤",title:"Chef de Projet",desc:"Personne responsable d'atteindre les objectifs du projet"},
    ],
    quiz:{ q:"Laquelle des options MIEUX décrit la triple contrainte en gestion de projet ?",
      options:["Périmètre, Qualité et Risque","Périmètre, Délai et Coût","Budget, Équipe et Calendrier","Planification, Exécution et Clôture"],
      answer:1, explanation:"La Triple Contrainte (Triangle de Fer) se compose du Périmètre, du Délai et du Coût. La qualité se situe au centre et est affectée par tout changement apporté à l'un de ces trois éléments."},
  },
  {
    id:2, tag:"Initiation", title:"Initiation & Charte de Projet",
    duration:"20 min", level:"Débutant",
    intro:"L'initiation est la première phase de tout projet. C'est là que vous définissez ce qu'est le projet, pourquoi il est important, qui est impliqué et s'il est réalisable. Une initiation solide évite des erreurs coûteuses par la suite.",
    steps:[
      { name:"Identifier les Parties Prenantes", desc:"Qui compte et pourquoi",
        content:`Une <strong>partie prenante</strong> est toute personne pouvant affecter ou être affectée par le projet.<ul><li><strong>Internes</strong> : dirigeants, membres de l'équipe, département financier</li><li><strong>Externes</strong> : clients, fournisseurs, gouvernement, communautés</li></ul><div class="pm-callout"><strong>Enseignement clé</strong><p>Une partie prenante ignorée pendant l'initiation peut devenir un obstacle majeur lors de l'exécution. S'engager tôt coûte moins cher que de gérer les conflits plus tard.</p></div>`},
      { name:"Mener une Étude de Faisabilité", desc:"Le projet vaut-il la peine d'être réalisé ?",
        content:`Évaluez la faisabilité selon quatre dimensions :<ul><li><strong>Technique</strong> — Avons-nous les compétences et les outils ?</li><li><strong>Financière</strong> — Le ROI est-il positif ?</li><li><strong>Opérationnelle</strong> — Pouvons-nous absorber le changement ?</li><li><strong>Juridique/Réglementaire</strong> — Y a-t-il des exigences de conformité ?</li></ul>`},
      { name:"Rédiger la Charte de Projet", desc:"L'acte de naissance de votre projet",
        content:`La <strong>Charte de Projet</strong> autorise officiellement le projet et donne au chef de projet le pouvoir d'utiliser les ressources. Elle doit inclure :<ul><li>Objet et objectifs du projet</li><li>Périmètre général et livrables</li><li>Estimations budgétaires et calendrier</li><li>Parties prenantes clés et rôles</li><li>Critères de succès et KPI</li><li>Hypothèses, contraintes et risques</li></ul>`},
      { name:"Définir les Critères de Succès", desc:"Comment saurez-vous que vous avez réussi ?",
        content:`Les critères de succès doivent être <strong>SMART</strong> : Spécifiques, Mesurables, Atteignables, Pertinents, Temporels.<br/>Exemple : "Déployer la plateforme LMS à 50 écoles avec 99,5 % de disponibilité d'ici le T4 2026" est bien plus précis que "créer une plateforme d'apprentissage".`},
    ],
    concepts:[
      {icon:"📜",title:"Charte de Projet",desc:"Document formel autorisant le projet"},
      {icon:"🔍",title:"Étude de Faisabilité",desc:"Analyse de la viabilité du projet"},
      {icon:"👥",title:"Registre des Parties Prenantes",desc:"Catalogue de toutes les parties prenantes"},
      {icon:"✅",title:"Objectifs SMART",desc:"Critères pour des objectifs bien définis"},
    ],
    quiz:{ q:"Quel est l'objectif PRINCIPAL d'une Charte de Projet ?",
      options:["Attribuer les tâches quotidiennes à l'équipe","Autoriser formellement le projet et donner autorité au chef de projet","Créer le calendrier du projet","Documenter les leçons apprises"],
      answer:1, explanation:"La Charte de Projet autorise officiellement le projet et accorde au chef de projet l'autorité d'utiliser les ressources organisationnelles. C'est le document de gouvernance fondamental."},
  },
  {
    id:3, tag:"Planification", title:"Fondements de la Planification de Projet",
    duration:"25 min", level:"Intermédiaire",
    intro:"La planification est là où la réussite ou l'échec d'un projet se décide. Un plan de projet approfondi sert de feuille de route — alignant l'équipe, fixant les attentes et fournissant une base de référence pour mesurer la performance tout au long de l'exécution.",
    steps:[
      { name:"Le Plan de Management de Projet", desc:"Votre plan directeur",
        content:`Le <strong>Plan de Management de Projet</strong> (PMP) intègre tous les plans subsidiaires :<ul><li>Plan de gestion du périmètre</li><li>Plan de gestion du calendrier</li><li>Plan de gestion des coûts</li><li>Plan de gestion de la qualité</li><li>Plan de gestion des risques</li><li>Plan de communication</li></ul><p>Le PMP est un document vivant — mis à jour à mesure que le projet évolue.</p>`},
      { name:"Définir le Périmètre", desc:"Ce qui est inclus et ce qui ne l'est pas",
        content:`Le <strong>périmètre</strong> définit l'ensemble des travaux requis :<ul><li><strong>Énoncé du périmètre</strong> — description détaillée des livrables</li><li><strong>Inclusions</strong> — ce qui EST inclus explicitement</li><li><strong>Exclusions</strong> — ce qui n'est PAS inclus explicitement</li><li><strong>Critères d'acceptation</strong> — conditions devant être remplies</li></ul><div class="pm-callout"><strong>Alerte glissement de périmètre</strong><p>Chaque "petite addition" non documentée érode le budget et le calendrier. Tout doit être documenté par écrit. Changement = demande de modification formelle.</p></div>`},
      { name:"Créer la SDP", desc:"Structure de Découpage du Projet — diviser pour mieux régner",
        content:`Une <strong>Structure de Découpage du Projet (SDP)</strong> décompose le périmètre en lots de travail gérables.<ul><li>Niveau 1 : Projet</li><li>Niveau 2 : Livrables principaux / phases</li><li>Niveau 3 : Lots de travail (assignables, estimables)</li></ul><p>La <strong>règle des 100 %</strong> : la SDP doit couvrir 100 % du travail du projet.</p>`},
      { name:"Estimer le Délai & le Coût", desc:"Des références réalistes pour le contrôle",
        content:`Techniques d'estimation courantes :<ul><li><strong>Analogique</strong> — utiliser des données de projets similaires passés</li><li><strong>Paramétrique</strong> — modèles statistiques</li><li><strong>Trois points</strong> — (Optimiste + Pessimiste + 4×Probable) ÷ 6</li><li><strong>Ascendante</strong> — estimer chaque tâche et consolider</li></ul><p>Toujours prévoir une <strong>réserve pour imprévus</strong> (risques connus) et une <strong>réserve de gestion</strong> (risques inconnus).</p>`},
    ],
    concepts:[
      {icon:"🗺️",title:"Plan de Management",desc:"Document maître intégrant tous les plans"},
      {icon:"📐",title:"SDP",desc:"Décomposition hiérarchique de tout le travail du projet"},
      {icon:"⏱️",title:"Estimation",desc:"Prédire le coût et la durée des travaux"},
      {icon:"🚫",title:"Exclusions de Périmètre",desc:"Ce qui n'est PAS inclus dans le projet"},
    ],
    quiz:{ q:"Que signifie la « règle des 100 % » dans le contexte d'une Structure de Découpage du Projet ?",
      options:["Tous les membres de l'équipe doivent travailler 100 % du temps","La SDP doit couvrir 100 % de l'ensemble du travail du projet","Le budget doit être 100 % alloué avant le début des travaux","Toutes les tâches doivent être terminées à 100 % avant la clôture"],
      answer:1, explanation:"La règle des 100 % signifie que la SDP doit inclure 100 % du travail défini dans le périmètre du projet — ni plus, ni moins. Rien ne doit être omis ou compté en double."},
  },
  {
    id:4, tag:"Planification", title:"Planification du Calendrier & Gestion des Délais",
    duration:"25 min", level:"Intermédiaire",
    intro:"Un calendrier transforme votre plan en chronologie. Il séquence les tâches, affecte les ressources et identifie le chemin critique qui détermine quand votre projet se terminera.",
    steps:[
      { name:"Séquencement des Activités", desc:"Les dépendances définissent votre flux",
        content:`Quatre types de dépendances de tâches :<ul><li><strong>Fin-Début (FD)</strong> — B ne peut pas commencer avant que A soit terminé (le plus courant)</li><li><strong>Début-Début (DD)</strong> — Les deux tâches démarrent simultanément</li><li><strong>Fin-Fin (FF)</strong> — Les deux tâches doivent se terminer ensemble</li><li><strong>Début-Fin (DF)</strong> — Rare ; B ne peut pas se terminer avant que A commence</li></ul>`},
      { name:"La Méthode du Chemin Critique", desc:"CPM — l'épine dorsale de votre projet",
        content:`Le <strong>Chemin Critique</strong> est la séquence la plus longue de tâches dépendantes et détermine la durée minimale du projet.<ul><li>Tout retard sur le chemin critique retarde l'ensemble du projet</li><li>Les tâches sur le chemin critique ont une <strong>marge nulle</strong></li><li>Les tâches non critiques ont une <strong>marge</strong> — elles peuvent glisser sans impacter la date de fin</li></ul><div class="pm-callout"><strong>Conseil CDP</strong><p>Concentrez votre suivi sur les tâches du chemin critique. Les tâches non critiques avec une grande marge peuvent attendre — les tâches critiques ne le peuvent pas.</p></div>`},
      { name:"Diagrammes de Gantt", desc:"Visualiser le calendrier",
        content:`Un <strong>diagramme de Gantt</strong> affiche les tâches sur une chronologie horizontale avec des barres montrant le début, la durée et la fin.<ul><li>Montre visuellement les dépendances entre tâches</li><li>Facile à communiquer aux parties prenantes</li><li>Met en évidence les jalons et les échéances</li></ul><p>Outils populaires : MS Project, Asana, Monday.com, Smartsheet.</p>`},
      { name:"Compression du Calendrier", desc:"Quand il faut aller plus vite",
        content:`Deux techniques :<ul><li><strong>Compression (Crashing)</strong> — Ajouter des ressources aux tâches du chemin critique. Augmente les coûts.</li><li><strong>Chevauchement (Fast Tracking)</strong> — Exécuter en parallèle des tâches initialement séquentielles. Augmente les risques.</li></ul><p>Toujours documenter les compromis avant de compresser.</p>`},
    ],
    concepts:[
      {icon:"🛤️",title:"Chemin Critique",desc:"Séquence la plus longue de tâches dépendantes"},
      {icon:"📊",title:"Diagramme de Gantt",desc:"Graphique visuel du calendrier du projet"},
      {icon:"⏳",title:"Marge",desc:"Temps pendant lequel une tâche peut glisser sans impacter la date de fin"},
      {icon:"🔗",title:"Dépendances",desc:"Relations qui déterminent l'ordre des tâches"},
    ],
    quiz:{ q:"Que signifie une « marge nulle » sur une tâche ?",
      options:["La tâche n'a pas de membre d'équipe assigné","La tâche n'a aucun coût","La tâche est sur le chemin critique et ne peut pas être retardée","La tâche est optionnelle"],
      answer:2, explanation:"Une tâche avec une marge nulle est sur le chemin critique — tout retard sur cette tâche retarde directement la date de fin du projet. Ces tâches nécessitent la surveillance la plus rigoureuse."},
  },
  {
    id:5, tag:"Ressources", title:"Gestion des Ressources",
    duration:"20 min", level:"Intermédiaire",
    intro:"Les personnes sont votre ressource la plus précieuse. La gestion des ressources couvre la manière dont vous acquérez, développez, gérez et optimisez votre équipe — ainsi que les ressources physiques et financières.",
    steps:[
      { name:"Planification des Ressources", desc:"De quoi et de qui avez-vous besoin ?",
        content:`La planification des ressources répond à trois questions :<ul><li><strong>Quelles</strong> ressources sont nécessaires ? (personnes, équipement, matériaux)</li><li><strong>Combien</strong> en faut-il ?</li><li><strong>Quand</strong> en a-t-on besoin ?</li></ul><p>Construisez une <strong>Structure de Découpage des Ressources (SDR)</strong> qui reflète votre SDP.</p>`},
      { name:"Constituer votre Équipe", desc:"Rôles, responsabilités & matrice RACI",
        content:`Une <strong>matrice RACI</strong> clarifie la responsabilité :<ul><li><strong>R</strong> — Responsable : effectue le travail</li><li><strong>A</strong> — Approbateur : est propriétaire du résultat (un seul par tâche)</li><li><strong>C</strong> — Consulté : fournit des contributions</li><li><strong>I</strong> — Informé : tenu au courant</li></ul><div class="pm-callout"><strong>Règle</strong><p>Chaque tâche doit avoir exactement UN Approbateur. Plusieurs Approbateurs = personne n'est vraiment responsable.</p></div>`},
      { name:"Nivellement des Ressources", desc:"Éliminer la sur-allocation",
        content:`Le nivellement des ressources résout les conflits lorsque des membres de l'équipe sont sur-alloués :<ul><li>Retarder les tâches non critiques jusqu'à ce que les ressources soient libérées</li><li>Fractionner les tâches sur plusieurs périodes</li><li>Embaucher ou sous-traiter des ressources supplémentaires</li></ul>`},
      { name:"Motivation & Développement de l'Équipe", desc:"Le modèle de Tuckman",
        content:`Les étapes de développement d'équipe selon Tuckman :<ul><li><strong>Formation (Forming)</strong> — courtoisie, rôles peu clairs</li><li><strong>Tension (Storming)</strong> — conflits entre personnalités</li><li><strong>Normalisation (Norming)</strong> — règles émergent, confiance se construit</li><li><strong>Performance (Performing)</strong> — production élevée, autonomie</li><li><strong>Dissolution (Adjourning)</strong> — le projet se termine, l'équipe se disperse</li></ul>`},
    ],
    concepts:[
      {icon:"👷",title:"Décomposition des Ressources",desc:"Liste hiérarchique de toutes les ressources du projet"},
      {icon:"📋",title:"Matrice RACI",desc:"Cadre d'attribution des responsabilités"},
      {icon:"⚖️",title:"Nivellement des Ressources",desc:"Résolution des conflits de sur-allocation"},
      {icon:"🔄",title:"Modèle de Tuckman",desc:"Étapes du développement d'une équipe"},
    ],
    quiz:{ q:"Dans une matrice RACI, quel rôle doit avoir exactement UNE personne assignée par tâche ?",
      options:["Responsable","Approbateur","Consulté","Informé"],
      answer:1, explanation:"Chaque tâche doit avoir exactement UN Approbateur. Cette personne est propriétaire du résultat. Plusieurs Approbateurs crée de la confusion et dilue la responsabilité."},
  },
  {
    id:6, tag:"Risques", title:"Gestion des Risques",
    duration:"25 min", level:"Intermédiaire",
    intro:"Tout projet fait face à l'incertitude. La gestion des risques est le processus systématique d'identification, d'analyse et de réponse aux risques du projet — menaces comme opportunités — pour protéger les résultats.",
    steps:[
      { name:"Identification des Risques", desc:"On ne peut pas gérer ce qu'on ne connaît pas",
        content:`L'identification des risques doit être un exercice d'équipe. Techniques courantes :<ul><li><strong>Brainstorming</strong> — génération de risques libre et sans jugement</li><li><strong>Analyse SWOT</strong> — Forces, Faiblesses, Opportunités, Menaces</li><li><strong>Interviews d'experts</strong> — capitaliser sur l'expérience du domaine</li><li><strong>Listes de contrôle</strong> — bibliothèques de risques spécifiques au secteur</li></ul><p>Documenter chaque risque dans un <strong>Registre des Risques</strong>.</p>`},
      { name:"Analyse Qualitative des Risques", desc:"Prioriser par probabilité et impact",
        content:`Évaluez chaque risque sur deux échelles :<ul><li><strong>Probabilité</strong> : Très Faible (5 %) → Très Élevée (90 %)</li><li><strong>Impact</strong> : Négligeable → Catastrophique</li></ul><p>Tracez sur une <strong>Matrice Probabilité-Impact</strong>.</p><div class="pm-callout"><strong>Zones de priorité</strong><p>Rouge = répondre immédiatement. Jaune = surveiller et planifier. Vert = accepter ou surveiller passivement.</p></div>`},
      { name:"Stratégies de Réponse aux Risques", desc:"Quatre façons de traiter les menaces",
        content:`Pour les menaces (risques négatifs) :<ul><li><strong>Éviter</strong> — éliminer le risque entièrement</li><li><strong>Transférer</strong> — transférer l'impact à un tiers (assurance)</li><li><strong>Atténuer</strong> — réduire la probabilité ou l'impact</li><li><strong>Accepter</strong> — reconnaître et budgétiser</li></ul><p>Pour les opportunités : Exploiter, Partager, Améliorer, ou Accepter.</p>`},
      { name:"Suivi & Contrôle des Risques", desc:"Les risques ne se gèrent pas seuls",
        content:`La gestion des risques est continue tout au long du projet :<ul><li>Réviser le Registre des Risques à chaque réunion de suivi</li><li>Mettre à jour les évaluations probabilité/impact selon l'évolution des conditions</li><li>Identifier de nouveaux risques à mesure que les travaux avancent</li><li>Exécuter les plans de contingence lorsque les déclencheurs se manifestent</li></ul>`},
    ],
    concepts:[
      {icon:"📊",title:"Registre des Risques",desc:"Document vivant répertoriant tous les risques identifiés"},
      {icon:"🎲",title:"Matrice P-I",desc:"Grille Probabilité-Impact pour la priorisation"},
      {icon:"🛡️",title:"Réponse aux Risques",desc:"Stratégie pour traiter chaque risque"},
      {icon:"🔔",title:"Déclencheurs de Risques",desc:"Indicateurs qu'un événement à risque s'est produit"},
    ],
    quiz:{ q:"Quelle stratégie de réponse aux risques consiste à transférer l'impact financier d'un risque à un tiers ?",
      options:["Éviter","Atténuer","Transférer","Accepter"],
      answer:2, explanation:"Le Transfert déplace la conséquence financière d'un risque vers une autre partie — généralement par le biais d'assurances, de cautionnements ou de contrats à prix fixe. Le risque existe mais l'organisation est protégée."},
  },
  {
    id:7, tag:"Exécution", title:"Exécution & Contrôle du Projet",
    duration:"25 min", level:"Avancé",
    intro:"L'exécution est l'endroit où les plans rencontrent la réalité. Cette phase implique de diriger et de gérer le travail du projet, tandis que le contrôle assure que la performance reste alignée sur le plan.",
    steps:[
      { name:"Diriger & Gérer les Travaux", desc:"Du plan à l'action",
        content:`Pendant l'exécution, les responsabilités clés du chef de projet :<ul><li>Attribuer les lots de travail aux membres de l'équipe</li><li>Lever les obstacles (être le bouclier de l'équipe)</li><li>Gérer la performance des fournisseurs et sous-traitants</li><li>Mettre en œuvre les modifications approuvées</li><li>Collecter continuellement les données de performance</li></ul>`},
      { name:"Gestion de la Valeur Acquise", desc:"L'étalon-or du contrôle de projet",
        content:`La <strong>GVA (Gestion de la Valeur Acquise)</strong> utilise trois métriques :<ul><li><strong>VA</strong> — Valeur Planifiée : coût budgété des travaux planifiés</li><li><strong>VR</strong> — Valeur Réalisée : coût budgété des travaux réellement achevés</li><li><strong>CR</strong> — Coût Réel : coût effectivement engagé</li></ul><p><strong>ÉC = VR − VA</strong> (Écart Calendrier) | <strong>EC = VR − CR</strong> (Écart Coût) | <strong>IPC = VR ÷ CR</strong></p><div class="pm-callout"><strong>Règle clé</strong><p>IPC inférieur à 1,0 = dépassement de budget. IPC et IPP négatifs = action immédiate requise.</p></div>`},
      { name:"Processus de Contrôle des Modifications", desc:"Gérer l'inévitable",
        content:`Chaque modification doit passer par un <strong>Contrôle Intégré des Modifications</strong> formel :<ul><li>Demande de modification soumise par écrit</li><li>Impact évalué (périmètre, délai, coût, risque)</li><li>Comité de Contrôle des Modifications (CCM) approuve ou rejette</li><li>Les modifications approuvées mettent à jour le PMP et les références de base</li></ul>`},
      { name:"Rapports d'Avancement", desc:"Maintenir les parties prenantes alignées",
        content:`Un bon rapport d'avancement comprend :<ul><li>Résumé des travaux réalisés sur la période</li><li>Métriques clés : calendrier, coût, périmètre (Rouge/Jaune/Vert)</li><li>Risques et problèmes nécessitant des décisions</li><li>Travaux à venir sur la prochaine période</li><li>Demandes de modification en attente</li></ul>`},
    ],
    concepts:[
      {icon:"📈",title:"Valeur Acquise",desc:"Mesure du travail réalisé par rapport au planifié"},
      {icon:"🔄",title:"Contrôle des Modifications",desc:"Processus formel d'approbation des changements"},
      {icon:"🚦",title:"Statut RAV",desc:"Indicateurs de santé Rouge/Ambre/Vert"},
      {icon:"📉",title:"Analyse des Écarts",desc:"Comparaison des réels avec la référence de base"},
    ],
    quiz:{ q:"Si l'IPC = 0,85 sur votre projet, qu'est-ce que cela indique ?",
      options:["Le projet est achevé à 85 %","Pour chaque 1 € dépensé, vous obtenez 0,85 € de valeur planifiée","Le projet bénéficie de 85 % d'approbation des parties prenantes","15 % des travaux ont été reprogrammés"],
      answer:1, explanation:"IPC = VR ÷ CR. Un IPC de 0,85 signifie que pour chaque euro réellement dépensé, seulement 0,85 € de travail planifié a été accompli — le projet est en dépassement de budget."},
  },
  {
    id:8, tag:"Communication", title:"Gestion de la Communication",
    duration:"15 min", level:"Intermédiaire",
    intro:"Des études montrent que les chefs de projet consacrent jusqu'à 90 % de leur temps à communiquer. Une gestion efficace de la communication garantit que la bonne information parvient aux bonnes personnes au bon moment.",
    steps:[
      { name:"Planifier la Communication", desc:"Qui doit savoir quoi, et quand ?",
        content:`Un <strong>Plan de Gestion de la Communication</strong> documente :<ul><li>Qui a besoin d'informations (public cible)</li><li>Quelles informations sont nécessaires</li><li>Quand et à quelle fréquence communiquer</li><li>Quels canaux utiliser (e-mail, réunions, tableaux de bord)</li></ul><p>Formule : <strong>N(N−1)/2</strong> = nombre de canaux de communication. Avec 10 personnes = 45 canaux.</p>`},
      { name:"Gestion des Réunions", desc:"Tirer le meilleur de chaque réunion",
        content:`Les réunions efficaces ont :<ul><li>Un ordre du jour clair envoyé 24 heures à l'avance</li><li>Un objectif défini (décision, avancement, résolution de problème)</li><li>Uniquement les participants essentiels</li><li>Un compte rendu avec les actions distribué dans les 24 heures</li></ul><div class="pm-callout"><strong>Adage CDP</strong><p>Sans ordre du jour, pas de réunion. Le temps est précieux sur un projet.</p></div>`},
      { name:"Résolution des Conflits", desc:"Le conflit est inévitable — le dysfonctionnement ne l'est pas",
        content:`Modes de gestion des conflits (Thomas-Kilmann) :<ul><li><strong>Collaboration</strong> — gagnant-gagnant ; recommandé par le PMI à long terme</li><li><strong>Compromis</strong> — gain partiel pour les deux parties ; bon pour la rapidité</li><li><strong>Accommodement</strong> — céder à l'autre partie ; à utiliser rarement</li><li><strong>Contrainte</strong> — imposer une décision ; détruit les relations</li><li><strong>Évitement</strong> — seulement pour les questions triviales</li></ul>`},
      { name:"Engagement des Parties Prenantes", desc:"Transformer la résistance en soutien",
        content:`Les parties prenantes progressent dans les niveaux d'engagement :<br/><strong>Ignorantes → Résistantes → Neutres → Favorables → Championnes</strong><br/><br/>Votre objectif : amener chaque partie prenante à au moins <strong>Favorable</strong>. Stratégies : entretiens individuels, implication précoce, reporting transparent, anticipation des préoccupations.`},
    ],
    concepts:[
      {icon:"📡",title:"Plan de Communication",desc:"Qui, quoi, quand, comment pour chaque partie prenante"},
      {icon:"🤝",title:"Résolution des Conflits",desc:"Approches structurées pour résoudre les désaccords"},
      {icon:"📣",title:"Engagement des PP",desc:"Faire progresser les parties prenantes vers le soutien"},
      {icon:"📝",title:"Compte Rendu de Réunion",desc:"Registre des actions issues des réunions de projet"},
    ],
    quiz:{ q:"Quelle formule calcule le nombre de canaux de communication dans une équipe projet ?",
      options:["N × 2","N(N−1)/2","N² − N","N + (N−1)"],
      answer:1, explanation:"La formule N(N−1)/2 donne le nombre total de canaux de communication. Avec 5 personnes = 10 canaux ; avec 10 personnes = 45 canaux. La complexité augmente rapidement à mesure que les équipes s'agrandissent."},
  },
  {
    id:9, tag:"Qualité", title:"Gestion de la Qualité",
    duration:"20 min", level:"Intermédiaire",
    intro:"La gestion de la qualité garantit que le projet satisfera les besoins pour lesquels il a été entrepris. Elle couvre à la fois la qualité du projet (comment le travail est réalisé) et la qualité du produit (ce qui est livré).",
    steps:[
      { name:"Planification de la Qualité", desc:"Définir la qualité avant de la mesurer",
        content:`La planification de la qualité identifie les normes pertinentes et comment les satisfaire. Livrables :<ul><li><strong>Plan de Gestion de la Qualité</strong> — politiques, processus et rôles</li><li><strong>Métriques de Qualité</strong> — taux de défauts, disponibilité %, satisfaction client</li><li><strong>Listes de Contrôle</strong> — points de contrôle qualité au niveau des tâches</li></ul><p>Coût de la qualité = coûts de prévention + coûts d'évaluation (qui réduisent les coûts de défaillance coûteux).</p>`},
      { name:"Assurance Qualité", desc:"Suivons-nous les bons processus ?",
        content:`L'<strong>Assurance Qualité (AQ)</strong> est axée sur les processus et proactive — elle audite les processus utilisés pour produire les livrables.<ul><li>Audits de processus</li><li>Analyse des causes profondes</li><li>Amélioration continue des processus</li></ul><p>L'AQ prévient les défauts avant qu'ils se produisent.</p>`},
      { name:"Contrôle Qualité", desc:"Nos livrables respectent-ils les normes ?",
        content:`Le <strong>Contrôle Qualité (CQ)</strong> est axé sur les produits et réactif — il inspecte les livrables réels par rapport aux normes définies.<ul><li>Tests et inspections</li><li>Suivi et reporting des défauts</li><li>Cartes de contrôle et diagrammes d'Ishikawa</li></ul><div class="pm-callout"><strong>Distinction clé</strong><p>AQ = faire les bonnes choses. CQ = vérifier que les choses ont été bien faites. Les deux sont indispensables.</p></div>`},
      { name:"Amélioration Continue", desc:"Ne jamais cesser de s'améliorer",
        content:`Cadres de qualité pour l'amélioration continue :<ul><li><strong>Kaizen</strong> — petites améliorations quotidiennes et incrémentales</li><li><strong>Cycle PDCA</strong> — Planifier, Faire, Vérifier, Agir (Cycle de Deming)</li><li><strong>Six Sigma</strong> — réduction des défauts basée sur les données</li><li><strong>Lean</strong> — éliminer le gaspillage et maximiser la valeur</li></ul>`},
    ],
    concepts:[
      {icon:"✅",title:"Métriques de Qualité",desc:"Mesures spécifiques pour la qualité du produit/processus"},
      {icon:"🔍",title:"Assurance Qualité",desc:"Audit des processus pour prévenir les défauts"},
      {icon:"🧪",title:"Contrôle Qualité",desc:"Inspection des livrables pour détecter les défauts"},
      {icon:"🔄",title:"Cycle PDCA",desc:"Boucle d'amélioration continue Planifier-Faire-Vérifier-Agir"},
    ],
    quiz:{ q:"Quelle est la différence essentielle entre l'Assurance Qualité et le Contrôle Qualité ?",
      options:["L'AQ est réalisée par des ingénieurs ; le CQ par des managers","L'AQ audite les processus pour prévenir les défauts ; le CQ inspecte les livrables pour les trouver","L'AQ se produit en fin de projet ; le CQ au début","Il n'y a pas de différence significative entre eux"],
      answer:1, explanation:"L'AQ est axée sur les processus et proactive — elle prévient les défauts. Le CQ est axé sur les produits et réactif — il trouve et corrige les défauts. Les deux sont essentiels."},
  },
  {
    id:10, tag:"Clôture", title:"Clôture de Projet & Retours d'Expérience",
    duration:"15 min", level:"Tous Niveaux",
    intro:"La clôture de projet est souvent la phase la plus négligée — pourtant c'est là que les organisations récoltent la valeur de leur investissement. Une clôture rigoureuse protège l'équipe, satisfait les parties prenantes et enrichit les projets futurs de connaissances institutionnelles.",
    steps:[
      { name:"Acceptation Formelle", desc:"Obtenir la signature du client",
        content:`L'acceptation formelle confirme que tous les livrables répondent aux critères d'acceptation convenus.<ul><li>Présenter tous les livrables au client</li><li>Obtenir une validation écrite de chacun</li><li>Documenter les points en suspens et convenir des délais de résolution</li><li>Transférer la propriété des actifs, de la documentation et des accès</li></ul>`},
      { name:"Clôture des Contrats", desc:"Clore chaque fil juridique",
        content:`Tous les contrats fournisseurs et sous-traitants doivent être formellement clôturés :<ul><li>Vérifier que tous les livrables contractuels ont été reçus</li><li>Confirmer que les dernières factures sont payées</li><li>Libérer les ressources conformément aux termes contractuels</li><li>Documenter la performance des fournisseurs pour référence future</li></ul>`},
      { name:"Retours d'Expérience", desc:"Le livrable le plus précieux de tout projet",
        content:`Une session de retours d'expérience structurée couvre :<ul><li>Qu'est-ce qui était planifié vs. ce qui s'est réellement passé ?</li><li>Quelles étaient les causes profondes des écarts significatifs ?</li><li>Que devons-nous commencer, arrêter ou continuer à faire ?</li><li>Quelle connaissance doit être préservée pour les futurs chefs de projet ?</li></ul><div class="pm-callout"><strong>Enseignement critique</strong><p>Les organisations qui sautent les retours d'expérience sont condamnées à répéter des erreurs coûteuses. Archivez-les — les chefs de projet futurs en dépendent.</p></div>`},
      { name:"Reconnaissance & Libération de l'Équipe", desc:"Terminer en beauté",
        content:`La dernière responsabilité du chef de projet est envers l'équipe :<ul><li>Reconnaître et célébrer formellement les contributions</li><li>Rédiger les évaluations de performance et les recommandations</li><li>Aider les membres de l'équipe à transitionner vers leurs prochaines missions</li><li>Archiver toute la documentation du projet selon les politiques de conservation</li></ul>`},
    ],
    concepts:[
      {icon:"✍️",title:"Acceptation Formelle",desc:"Validation écrite de tous les livrables du projet"},
      {icon:"📚",title:"Retours d'Expérience",desc:"Connaissances documentées pour les projets futurs"},
      {icon:"🤝",title:"Clôture des Contrats",desc:"Clôture formelle des accords fournisseurs"},
      {icon:"🏆",title:"Reconnaissance de l'Équipe",desc:"Célébrer les contributions et libérer les ressources"},
    ],
    quiz:{ q:"Pourquoi l'acceptation écrite formelle des livrables est-elle critique lors de la clôture du projet ?",
      options:["Elle déclenche le paiement final du chef de projet","Elle confirme que les livrables répondent aux critères et protège contre les futures réclamations de responsabilité","Elle n'est requise que pour les contrats gouvernementaux","Elle permet à l'équipe de commencer le prochain projet"],
      answer:1, explanation:"L'acceptation écrite formelle confirme que tous les livrables répondent aux critères d'acceptation convenus. Sans elle, l'organisation peut rester responsable des problèmes continus après la fin du projet."},
  },
];

/* ═══════════════════════════════════════════════════════════
   SIDEBAR GROUPS
═══════════════════════════════════════════════════════════ */
const GROUPS_EN = [
  { label:"Foundations & Initiation", ids:[1,2] },
  { label:"Planning & Scheduling", ids:[3,4] },
  { label:"Execution & Control", ids:[5,6,7] },
  { label:"Communication & Quality", ids:[8,9] },
  { label:"Project Closure", ids:[10] },
];
const GROUPS_FR = [
  { label:"Fondements & Initiation", ids:[1,2] },
  { label:"Planification & Calendrier", ids:[3,4] },
  { label:"Exécution & Contrôle", ids:[5,6,7] },
  { label:"Communication & Qualité", ids:[8,9] },
  { label:"Clôture de Projet", ids:[10] },
];

/* ═══════════════════════════════════════════════════════════
   INJECT STYLES
═══════════════════════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
.pm-app{display:flex;height:100vh;overflow:hidden;font-family:'DM Sans',sans-serif;background:#f4f6fa;color:#1a2a40}
.pm-sidebar{width:292px;min-width:292px;background:#0D1F3C;display:flex;flex-direction:column;overflow:hidden;transition:width 0.3s}
.pm-main{flex:1;display:flex;flex-direction:column;overflow:hidden;min-width:0}
.pm-sidebar-header{padding:22px 18px 16px;border-bottom:1px solid rgba(201,168,76,0.2)}
.pm-logo{font-family:'Playfair Display',serif;color:#C9A84C;font-size:17px;font-weight:700}
.pm-logo-sub{color:rgba(255,255,255,0.4);font-size:10px;text-transform:uppercase;letter-spacing:1.5px;margin-top:3px}
.pm-prog-wrap{padding:14px 18px;border-bottom:1px solid rgba(255,255,255,0.07)}
.pm-prog-label{display:flex;justify-content:space-between;margin-bottom:7px;font-size:11px;color:rgba(255,255,255,0.5)}
.pm-prog-label span:last-child{color:#C9A84C;font-weight:600}
.pm-prog-bar{height:3px;background:rgba(255,255,255,0.1);border-radius:2px;overflow:hidden}
.pm-prog-fill{height:100%;background:linear-gradient(90deg,#C9A84C,#E8C96A);border-radius:2px;transition:width 0.4s ease}
.pm-mod-list{flex:1;overflow-y:auto;padding:10px 0;scrollbar-width:thin;scrollbar-color:rgba(255,255,255,0.1) transparent}
.pm-mod-group-header{padding:10px 18px 4px;color:rgba(255,255,255,0.25);font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px}
.pm-mod-item{padding:9px 18px;cursor:pointer;display:flex;align-items:center;gap:10px;border-left:3px solid transparent;transition:background 0.15s}
.pm-mod-item:hover{background:rgba(255,255,255,0.05)}
.pm-mod-item.active{background:rgba(201,168,76,0.12);border-left-color:#C9A84C}
.pm-mod-item.completed .pm-mod-num{background:#2db37a!important;color:#fff!important}
.pm-mod-num{width:24px;height:24px;border-radius:50%;background:rgba(255,255,255,0.08);color:rgba(255,255,255,0.4);font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all 0.2s}
.pm-mod-item.active .pm-mod-num{background:#C9A84C;color:#0D1F3C}
.pm-mod-title{font-size:12px;color:rgba(255,255,255,0.55);line-height:1.3}
.pm-mod-item.active .pm-mod-title{color:#fff;font-weight:500}
.pm-mod-check{margin-left:auto;color:#2db37a;font-size:11px}
.pm-topbar{background:#fff;border-bottom:1px solid #dde4ef;padding:0 28px;height:58px;display:flex;align-items:center;justify-content:space-between;flex-shrink:0;gap:8px}
.pm-breadcrumb{font-size:12px;color:#6b7fa0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.pm-breadcrumb span{color:#1a2a40;font-weight:600}
.pm-topbar-right{display:flex;gap:7px;align-items:center;flex-shrink:0}
.pm-btn{padding:7px 14px;border-radius:6px;font-size:12px;font-weight:600;cursor:pointer;border:none;font-family:inherit;transition:all 0.15s;white-space:nowrap}
.pm-btn-outline{background:transparent;border:1.5px solid #dde4ef;color:#1a2a40}
.pm-btn-outline:hover{border-color:#0D1F3C}
.pm-btn-outline:disabled{opacity:0.35;cursor:not-allowed}
.pm-btn-gold{background:#C9A84C;color:#0D1F3C}
.pm-btn-gold:hover{background:#E8C96A}
.pm-btn-navy{background:#0D1F3C;color:#fff}
.pm-btn-navy:hover{background:#1e3a5f}
.pm-btn-ghost{background:rgba(13,31,60,0.06);color:#0D1F3C;border:none}
.pm-btn-ghost:hover{background:rgba(13,31,60,0.1)}
.pm-btn-sm{padding:5px 11px;font-size:11px}
.pm-scroll{flex:1;overflow-y:auto;padding:32px 40px;scrollbar-width:thin;scrollbar-color:#dde4ef transparent}
.pm-lesson-tag{display:inline-flex;align-items:center;background:#f5edd8;color:#C9A84C;font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;padding:4px 11px;border-radius:20px;margin-bottom:12px}
.pm-lesson-title{font-family:'Playfair Display',serif;font-size:28px;font-weight:700;color:#0D1F3C;line-height:1.2;margin-bottom:10px}
.pm-lesson-meta{display:flex;gap:14px;align-items:center;margin-bottom:24px}
.pm-meta-badge{font-size:12px;color:#6b7fa0;font-weight:500}
.pm-intro{background:#fff;border:1px solid #dde4ef;border-radius:10px;padding:18px 22px;margin-bottom:20px;border-left:4px solid #C9A84C;font-size:14px;line-height:1.7;color:#1a2a40}
.pm-step-card{background:#fff;border:1px solid #dde4ef;border-radius:10px;overflow:hidden;margin-bottom:12px}
.pm-step-header{padding:14px 18px;display:flex;align-items:center;gap:12px;cursor:pointer;user-select:none}
.pm-step-num{width:32px;height:32px;border-radius:7px;background:#0D1F3C;color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.pm-step-name{font-size:13px;font-weight:600;color:#0D1F3C}
.pm-step-desc{font-size:11px;color:#6b7fa0;margin-top:2px}
.pm-step-chev{margin-left:auto;color:#6b7fa0;transition:transform 0.2s;font-size:15px}
.pm-step-chev.open{transform:rotate(180deg)}
.pm-step-body{padding:0 18px 18px 62px;font-size:13px;line-height:1.75;color:#3a4a60}
.pm-step-body ul{padding-left:16px;margin:8px 0}
.pm-step-body li{margin-bottom:4px}
.pm-step-body li::marker{color:#C9A84C}
.pm-step-body p{margin-bottom:8px}
.pm-callout{background:#f4f6fa;border-radius:7px;padding:11px 14px;margin-top:12px;border-left:3px solid #1e3a5f}
.pm-callout strong{color:#0D1F3C;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;display:block;margin-bottom:3px}
.pm-callout p{margin:0;font-size:12px;color:#6b7fa0}
.pm-concepts-label{font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:#6b7fa0;font-weight:700;margin:22px 0 10px}
.pm-concepts-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:20px}
.pm-concept-card{background:#fff;border:1px solid #dde4ef;border-radius:9px;padding:14px 16px}
.pm-concept-icon{font-size:18px;margin-bottom:5px}
.pm-concept-title{font-size:12px;font-weight:700;color:#0D1F3C;margin-bottom:2px}
.pm-concept-desc{font-size:11px;color:#6b7fa0;line-height:1.4}
.pm-quiz{background:#fff;border:1px solid #dde4ef;border-radius:10px;padding:22px;margin-bottom:20px}
.pm-quiz-title{font-family:'Playfair Display',serif;font-size:18px;color:#0D1F3C;margin-bottom:3px}
.pm-quiz-sub{font-size:12px;color:#6b7fa0;margin-bottom:16px}
.pm-quiz-q{font-size:14px;font-weight:600;color:#0D1F3C;margin-bottom:12px}
.pm-quiz-opts{display:flex;flex-direction:column;gap:8px}
.pm-quiz-opt{padding:10px 13px;border:1.5px solid #dde4ef;border-radius:7px;font-size:13px;cursor:pointer;transition:all 0.15s;color:#1a2a40;text-align:left;background:#fff;font-family:inherit}
.pm-quiz-opt:hover:not(:disabled){border-color:#0D1F3C;background:rgba(13,31,60,0.02)}
.pm-quiz-opt.selected{border-color:#C9A84C;background:#f5edd8;font-weight:500}
.pm-quiz-opt.correct{border-color:#2db37a;background:rgba(45,179,122,0.08);color:#1a7a4d;font-weight:600}
.pm-quiz-opt.wrong{border-color:#e05a5a;background:rgba(224,90,90,0.06);color:#c03030}
.pm-quiz-feedback{margin-top:12px;padding:10px 13px;border-radius:7px;font-size:12px;font-weight:500}
.pm-quiz-feedback.correct{background:rgba(45,179,122,0.1);color:#1a7a4d}
.pm-quiz-feedback.wrong{background:rgba(224,90,90,0.08);color:#c03030}
.pm-complete-btn{margin-top:12px;display:flex;justify-content:flex-end}
.pm-footer{background:#fff;border-top:1px solid #dde4ef;padding:12px 40px;display:flex;align-items:center;justify-content:space-between;flex-shrink:0}
.pm-dots{display:flex;gap:5px}
.pm-dot{width:6px;height:6px;border-radius:50%;background:#dde4ef;transition:all 0.2s;cursor:pointer}
.pm-dot:hover{background:#aab}
.pm-dot.active{background:#C9A84C;width:16px;border-radius:3px}
.pm-dot.done{background:#2db37a}
.pm-footer-label{font-size:11px;color:#6b7fa0}
.pm-done-banner{background:linear-gradient(135deg,#0D1F3C,#1e3a5f);border-radius:10px;padding:26px;text-align:center;margin-bottom:22px;color:#fff}
.pm-done-banner h2{font-family:'Playfair Display',serif;font-size:22px;margin-bottom:7px}
.pm-done-banner p{color:rgba(255,255,255,0.55);font-size:13px;margin-bottom:18px}

/* SCORE PANEL */
.pm-score-overlay{position:fixed;inset:0;background:rgba(13,31,60,0.5);z-index:100;display:flex;align-items:center;justify-content:center;padding:20px}
.pm-score-panel{background:#fff;border-radius:14px;width:100%;max-width:540px;max-height:80vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,0.2)}
.pm-score-header{padding:20px 24px;border-bottom:1px solid #dde4ef;display:flex;align-items:center;justify-content:space-between}
.pm-score-title{font-family:'Playfair Display',serif;font-size:20px;color:#0D1F3C}
.pm-score-body{padding:20px 24px;overflow-y:auto;flex:1}
.pm-score-summary{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:20px}
.pm-score-stat{background:#f4f6fa;border-radius:8px;padding:14px;text-align:center}
.pm-score-stat-val{font-family:'Playfair Display',serif;font-size:26px;font-weight:700;color:#0D1F3C}
.pm-score-stat-label{font-size:10px;color:#6b7fa0;text-transform:uppercase;letter-spacing:0.8px;margin-top:3px}
.pm-score-stat.gold .pm-score-stat-val{color:#C9A84C}
.pm-score-stat.green .pm-score-stat-val{color:#2db37a}
.pm-score-table{width:100%;border-collapse:collapse;font-size:12px}
.pm-score-table th{text-align:left;padding:8px 10px;color:#6b7fa0;font-weight:600;text-transform:uppercase;letter-spacing:0.8px;font-size:10px;border-bottom:1px solid #dde4ef}
.pm-score-table td{padding:9px 10px;border-bottom:1px solid #f0f2f6;color:#1a2a40;vertical-align:middle}
.pm-score-table tr:last-child td{border-bottom:none}
.pm-score-badge{display:inline-flex;align-items:center;gap:4px;padding:3px 9px;border-radius:20px;font-size:11px;font-weight:600}
.pm-score-badge.correct{background:rgba(45,179,122,0.1);color:#1a7a4d}
.pm-score-badge.wrong{background:rgba(224,90,90,0.08);color:#c03030}
.pm-score-badge.pending{background:#f0f2f6;color:#6b7fa0}

/* CERTIFICATE MODAL */
.pm-cert-overlay{position:fixed;inset:0;background:rgba(13,31,60,0.6);z-index:200;display:flex;align-items:center;justify-content:center;padding:20px}
.pm-cert-modal{background:#fff;border-radius:14px;width:100%;max-width:480px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,0.25)}
.pm-cert-header{background:#0D1F3C;padding:20px 24px;display:flex;align-items:center;justify-content:space-between}
.pm-cert-header h3{font-family:'Playfair Display',serif;color:#C9A84C;font-size:18px}
.pm-cert-body{padding:24px}
.pm-cert-label{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:#6b7fa0;margin-bottom:6px}
.pm-cert-input{width:100%;padding:10px 13px;border:1.5px solid #dde4ef;border-radius:7px;font-size:14px;font-family:inherit;color:#1a2a40;outline:none;transition:border 0.15s;box-sizing:border-box}
.pm-cert-input:focus{border-color:#C9A84C}
.pm-cert-lang-toggle{display:flex;gap:6px;margin-top:8px}
.pm-cert-lang-btn{flex:1;padding:8px;border:1.5px solid #dde4ef;border-radius:6px;font-size:12px;font-weight:600;cursor:pointer;background:#fff;color:#6b7fa0;font-family:inherit;transition:all 0.15s}
.pm-cert-lang-btn.active{border-color:#C9A84C;background:#f5edd8;color:#0D1F3C}
.pm-cert-preview{margin:16px 0;background:#f4f6fa;border-radius:8px;padding:14px;font-size:12px;color:#6b7fa0;line-height:1.6}
.pm-cert-preview strong{color:#0D1F3C}
.pm-cert-actions{display:flex;gap:8px;margin-top:4px}
`;

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function ProjectManagementCoursePro({ workerId, courseId, onBack }) {
  const [lang, setLang] = useState("en");
  const [current, setCurrent] = useState(0);
  const [completed, setCompleted] = useState(new Set());
  const [openStep, setOpenStep] = useState(0);
  const [quizAnswered, setQuizAnswered] = useState(false);
  const [quizSelected, setQuizSelected] = useState(null);
  const [scores, setScores] = useState({});
  const [showScore, setShowScore] = useState(false);
  const [showCert, setShowCert] = useState(false);
  const [certName, setCertName] = useState("");
  const [certLang, setCertLang] = useState("en");
  const [jsPDFLoaded, setJsPDFLoaded] = useState(false);

  // Load progress from DB on mount
  useEffect(() => {
    if (!workerId || !courseId) return
    import('../services/api').then(({ courseProgressAPI }) => {
      courseProgressAPI.getByWorker(workerId).then(all => {
        const prog = all.find(p => (p.course_id ?? p.courseId) === courseId)
        if (prog) {
          setCurrent(prog.current_idx ?? prog.current ?? 0)
          setCompleted(new Set(prog.completed || []))
          setScores(prog.scores || {})
        }
      }).catch(console.error)
    })
  }, [workerId, courseId])

  // Save progress to DB whenever it changes
  useEffect(() => {
    if (!workerId || !courseId) return
    const pct = Math.round((completed.size / 10) * 100)
    const data = { current, completed: [...completed], scores, pct }
    import('../services/api').then(({ courseProgressAPI }) => {
      courseProgressAPI.save(workerId, courseId, data).catch(console.error)
    })
  }, [current, completed, scores, workerId, courseId])

  const MODULES = lang === "en" ? MODULES_EN : MODULES_FR;
  const GROUPS = lang === "en" ? GROUPS_EN : GROUPS_FR;
  const t = UI[lang];
  const mod = MODULES[current];
  const pct = Math.round((completed.size / MODULES.length) * 100);
  const totalCorrect = Object.values(scores).filter(s => s.correct).length;

  // Inject CSS
  useEffect(() => {
    let el = document.getElementById("pm-pro-styles");
    if (!el) {
      el = document.createElement("style");
      el.id = "pm-pro-styles";
      document.head.appendChild(el);
    }
    el.textContent = CSS;
    return () => el.remove();
  }, []);

  // jsPDF loaded via npm import
  useEffect(() => { setJsPDFLoaded(true); }, []);

  const goTo = useCallback((idx) => {
    setCurrent(idx);
    setOpenStep(0);
    setQuizAnswered(false);
    setQuizSelected(null);
  }, []);

  const navigate = (dir) => {
    const next = current + dir;
    if (next < 0 || next >= MODULES.length) return;
    goTo(next);
  };

  const checkQuiz = () => {
    setQuizAnswered(true);
    const correct = quizSelected === mod.quiz.answer;
    setScores(prev => ({ ...prev, [current]: { selected: quizSelected, correct } }));
  };

  const completeModule = () => {
    setCompleted(prev => new Set([...prev, current]));
    if (current < MODULES.length - 1) goTo(current + 1);
  };

  const allDone = completed.size === MODULES.length;

  /* ── Certificate PDF Generator ── */
  const generateCertificate = () => {
    if (!jsPDFLoaded) {
      alert("PDF library is still loading, please try again in a moment.");
      return;
    }
    const name = certName.trim() || (certLang === "en" ? "Student" : "Étudiant(e)");
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const W = 297, H = 210;

    // Background
    doc.setFillColor(13, 31, 60);
    doc.rect(0, 0, W, H, "F");

    // Gold border outer
    doc.setDrawColor(201, 168, 76);
    doc.setLineWidth(1.2);
    doc.rect(8, 8, W - 16, H - 16);

    // Gold border inner
    doc.setLineWidth(0.4);
    doc.rect(11, 11, W - 22, H - 22);

    // Corner ornaments
    const corners = [[14,14],[W-14,14],[14,H-14],[W-14,H-14]];
    corners.forEach(([x,y]) => {
      doc.setFillColor(201, 168, 76);
      doc.circle(x, y, 2.5, "F");
    });

    // Gold top stripe
    doc.setFillColor(201, 168, 76);
    doc.rect(0, 0, W, 2.5, "F");
    doc.rect(0, H - 2.5, W, 2.5, "F");

    // Logo mark
    doc.setFillColor(201, 168, 76);
    doc.circle(W / 2, 28, 9, "F");
    doc.setTextColor(13, 31, 60);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("OL", W / 2, 31, { align: "center" });

    // Organization name
    doc.setTextColor(201, 168, 76);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text(certLang === "en" ? "ONBOARDLEARNING · GROUPE OUAMBO" : "ONBOARDLEARNING · GROUPE OUAMBO", W / 2, 45, { align: "center" });

    // Certificate heading
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setCharSpace(3);
    doc.text(
      certLang === "en" ? "CERTIFICATE OF COMPLETION" : "CERTIFICAT D'ACHÈVEMENT",
      W / 2, 58, { align: "center" }
    );
    doc.setCharSpace(0);

    // Decorative line
    doc.setDrawColor(201, 168, 76);
    doc.setLineWidth(0.4);
    doc.line(W / 2 - 55, 62, W / 2 + 55, 62);

    // "This certifies that" line
    doc.setTextColor(180, 200, 230);
    doc.setFontSize(10);
    doc.setFont("helvetica", "italic");
    doc.text(
      certLang === "en" ? "This is to certify that" : "Ceci certifie que",
      W / 2, 73, { align: "center" }
    );

    // Name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(26);
    doc.setFont("helvetica", "bold");
    doc.text(name, W / 2, 92, { align: "center" });

    // Underline name
    const nw = doc.getTextWidth(name);
    doc.setLineWidth(0.5);
    doc.setDrawColor(201, 168, 76);
    doc.line(W / 2 - nw / 2, 95, W / 2 + nw / 2, 95);

    // Completion statement
    doc.setTextColor(180, 200, 230);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const stmt = certLang === "en"
      ? "has successfully completed all 10 modules of the"
      : "a réussi l'ensemble des 10 modules du";
    doc.text(stmt, W / 2, 106, { align: "center" });

    // Course title
    doc.setTextColor(201, 168, 76);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    const courseTitle = certLang === "en"
      ? "Project Management Professional Certificate"
      : "Certificat Professionnel en Gestion de Projet";
    doc.text(courseTitle, W / 2, 118, { align: "center" });

    // Score badge
    const scoreText = certLang === "en"
      ? `Final Score: ${totalCorrect} / ${MODULES.length} (${Math.round((totalCorrect / MODULES.length) * 100)}%)`
      : `Score Final : ${totalCorrect} / ${MODULES.length} (${Math.round((totalCorrect / MODULES.length) * 100)} %)`;
    doc.setFillColor(30, 58, 95);
    doc.roundedRect(W / 2 - 45, 123, 90, 12, 3, 3, "F");
    doc.setTextColor(201, 168, 76);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text(scoreText, W / 2, 130.5, { align: "center" });

    // Date
    const now = new Date();
    const dateStr = now.toLocaleDateString(certLang === "en" ? "en-US" : "fr-FR", {
      year: "numeric", month: "long", day: "numeric"
    });
    doc.setTextColor(180, 200, 230);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(
      certLang === "en" ? `Issued on ${dateStr}` : `Délivré le ${dateStr}`,
      W / 2, 141, { align: "center" }
    );

    // Divider line
    doc.setDrawColor(201, 168, 76);
    doc.setLineWidth(0.3);
    doc.line(W / 2 - 70, 146, W / 2 + 70, 146);

    // Signature lines
    const sigY = 162;
    doc.setDrawColor(100, 130, 180);
    doc.setLineWidth(0.3);
    doc.line(W/2 - 75, sigY, W/2 - 25, sigY);
    doc.line(W/2 + 25, sigY, W/2 + 75, sigY);

    doc.setTextColor(140, 170, 210);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(certLang === "en" ? "Course Director" : "Directeur de Cours", W/2 - 50, sigY + 5, { align: "center" });
    doc.text(certLang === "en" ? "President, Groupe OUAMBO" : "Président, Groupe OUAMBO", W/2 + 50, sigY + 5, { align: "center" });

    // Footer
    doc.setTextColor(80, 110, 160);
    doc.setFontSize(7.5);
    doc.text("www.onboardlearning.org · www.groupeouambo.com", W / 2, H - 16, { align: "center" });

    doc.save(`${certLang === "en" ? "Certificate" : "Certificat"}_${name.replace(/\s+/g, "_")}_OnboardLearning.pdf`);
    setShowCert(false);
  };

  /* ── Score Panel Data ── */
  const scoreRows = MODULES_EN.map((m, i) => {
    const s = scores[i];
    return { id: m.id, titleEN: m.title, titleFR: MODULES_FR[i].title, score: s || null };
  });

  const scoreCorrectCount = Object.values(scores).filter(s => s.correct).length;
  const scoreAttempted = Object.keys(scores).length;

  return (
    <div className="pm-app">
      {/* ── SIDEBAR ── */}
      <div className="pm-sidebar">
        <div className="pm-sidebar-header">
          <div className="pm-logo-sub">{t.subtitle}</div>
        </div>

        <div className="pm-prog-wrap">
          <div className="pm-prog-label">
            <span>{t.progress}</span>
            <span>{pct}%</span>
          </div>
          <div className="pm-prog-bar">
            <div className="pm-prog-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="pm-mod-list">
          {GROUPS.map(g => (
            <div key={g.label}>
              <div className="pm-mod-group-header">{g.label}</div>
              {g.ids.map(id => {
                const idx = id - 1;
                const isDone = completed.has(idx);
                const isActive = idx === current;
                const s = scores[idx];
                return (
                  <div
                    key={id}
                    className={`pm-mod-item${isActive ? " active" : ""}${isDone ? " completed" : ""}`}
                    onClick={() => goTo(idx)}
                  >
                    <div className="pm-mod-num">{isDone ? "✓" : id}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="pm-mod-title" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {MODULES[idx].title}
                      </div>
                    </div>
                    {s && (
                      <span style={{ fontSize: 11, color: s.correct ? "#2db37a" : "#e05a5a", flexShrink: 0 }}>
                        {s.correct ? "✓" : "✗"}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* ── MAIN ── */}
      <div className="pm-main">
        {/* Top Bar */}
        <div className="pm-topbar">
          <div className="pm-breadcrumb">
            {onBack && (
              <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7fa0', fontSize: '13px', marginRight: '10px', padding: 0 }}>
                ← Back
              </button>
            )}
            {t.breadcrumb} · <span>{mod.title}</span>
          </div>
          <div className="pm-topbar-right">
            <button className="pm-btn pm-btn-ghost pm-btn-sm" onClick={() => setLang(l => l === "en" ? "fr" : "en")}>
              {t.langToggle}
            </button>
            <button className="pm-btn pm-btn-ghost pm-btn-sm" onClick={() => setShowScore(true)}>
              📊 {t.scorePanel}
            </button>
            <button className="pm-btn pm-btn-outline pm-btn-sm" onClick={() => navigate(-1)} disabled={current === 0}>
              {t.prevBtn}
            </button>
            <button className="pm-btn pm-btn-gold pm-btn-sm" onClick={() => navigate(1)} disabled={current === MODULES.length - 1}>
              {current === MODULES.length - 1 ? t.finishBtn : t.nextBtn}
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="pm-scroll">
          {/* Completion Banner */}
          {allDone && current === MODULES.length - 1 && (
            <div className="pm-done-banner">
              <div style={{ fontSize: 40, marginBottom: 8 }}>🎓</div>
              <h2>{t.allDoneTitle}</h2>
              <p>{t.allDoneMsg}</p>
              <button
                className="pm-btn pm-btn-gold"
                style={{ fontSize: 13, padding: "10px 22px" }}
                onClick={() => setShowCert(true)}
              >{t.certBtn}</button>
            </div>
          )}

          {/* Lesson Header */}
          <div className="pm-lesson-tag">📌 {mod.tag}</div>
          <div className="pm-lesson-title">{mod.title}</div>
          <div className="pm-lesson-meta">
            <span className="pm-meta-badge">⏱ {mod.duration}</span>
            <span style={{ color: "#dde4ef" }}>·</span>
            <span className="pm-meta-badge">📶 {mod.level}</span>
            <span style={{ color: "#dde4ef" }}>·</span>
            <span className="pm-meta-badge">📝 {mod.steps.length} {lang === "en" ? "steps" : "étapes"}</span>
          </div>
          <div className="pm-intro">{mod.intro}</div>

          {/* Steps */}
          {mod.steps.map((s, i) => (
            <div className="pm-step-card" key={i}>
              <div className="pm-step-header" onClick={() => setOpenStep(prev => prev === i ? -1 : i)}>
                <div className="pm-step-num">{i + 1}</div>
                <div style={{ flex: 1 }}>
                  <div className="pm-step-name">{s.name}</div>
                  <div className="pm-step-desc">{s.desc}</div>
                </div>
                <span className={`pm-step-chev${openStep === i ? " open" : ""}`}>▾</span>
              </div>
              {openStep === i && (
                <div className="pm-step-body" dangerouslySetInnerHTML={{ __html: s.content }} />
              )}
            </div>
          ))}

          {/* Key Concepts */}
          <div className="pm-concepts-label">{t.keyConcepts}</div>
          <div className="pm-concepts-grid">
            {mod.concepts.map((c, i) => (
              <div className="pm-concept-card" key={i}>
                <div className="pm-concept-icon">{c.icon}</div>
                <div className="pm-concept-title">{c.title}</div>
                <div className="pm-concept-desc">{c.desc}</div>
              </div>
            ))}
          </div>

          {/* Quiz */}
          {mod.quiz && (
            <div className="pm-quiz">
              <div className="pm-quiz-title">{t.quizTitle}</div>
              <div className="pm-quiz-sub">{t.quizSub}</div>
              <div className="pm-quiz-q">{mod.quiz.q}</div>
              <div className="pm-quiz-opts">
                {mod.quiz.options.map((opt, i) => {
                  let cls = "pm-quiz-opt";
                  if (quizAnswered && quizSelected === i) cls += i === mod.quiz.answer ? " correct" : " wrong";
                  else if (quizAnswered && i === mod.quiz.answer) cls += " correct";
                  else if (!quizAnswered && quizSelected === i) cls += " selected";
                  return (
                    <button key={i} className={cls} onClick={() => !quizAnswered && setQuizSelected(i)} disabled={quizAnswered}>
                      {opt}
                    </button>
                  );
                })}
              </div>

              {!quizAnswered && quizSelected !== null && (
                <div style={{ marginTop: 12 }}>
                  <button className="pm-btn pm-btn-navy" onClick={checkQuiz}>{t.checkBtn}</button>
                </div>
              )}

              {quizAnswered && (
                <>
                  <div className={`pm-quiz-feedback ${quizSelected === mod.quiz.answer ? "correct" : "wrong"}`}>
                    {quizSelected === mod.quiz.answer ? t.correct : t.wrong}{mod.quiz.explanation}
                  </div>
                  <div className="pm-complete-btn">
                    <button className="pm-btn pm-btn-gold" onClick={completeModule}>{t.markComplete}</button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Manual cert trigger if already completed */}
          {completed.has(current) && !allDone && (
            <div style={{ marginBottom: 20, display: "flex", justifyContent: "flex-end" }}>
              <button className="pm-btn pm-btn-ghost pm-btn-sm" onClick={() => navigate(1)}>
                {t.nextBtn}
              </button>
            </div>
          )}

          <div style={{ height: 32 }} />
        </div>

        {/* Footer Nav */}
        <div className="pm-footer">
          <div className="pm-dots">
            {MODULES.map((_, i) => (
              <div
                key={i}
                className={`pm-dot${i === current ? " active" : ""}${completed.has(i) && i !== current ? " done" : ""}`}
                onClick={() => goTo(i)}
                title={MODULES[i].title}
              />
            ))}
          </div>
          <div className="pm-footer-label">{t.moduleOf(current + 1, MODULES.length)}</div>
        </div>
      </div>

      {/* ── SCORE PANEL MODAL ── */}
      {showScore && (
        <div className="pm-score-overlay" onClick={() => setShowScore(false)}>
          <div className="pm-score-panel" onClick={e => e.stopPropagation()}>
            <div className="pm-score-header">
              <div className="pm-score-title">{t.scorePanel}</div>
              <button className="pm-btn pm-btn-outline pm-btn-sm" onClick={() => setShowScore(false)}>
                {t.scoreClose}
              </button>
            </div>
            <div className="pm-score-body">
              <div className="pm-score-summary">
                <div className="pm-score-stat">
                  <div className="pm-score-stat-val">{scoreAttempted}</div>
                  <div className="pm-score-stat-label">{lang === "en" ? "Attempted" : "Tentés"}</div>
                </div>
                <div className="pm-score-stat green">
                  <div className="pm-score-stat-val">{scoreCorrectCount}</div>
                  <div className="pm-score-stat-label">{t.scoreCorrect}</div>
                </div>
                <div className="pm-score-stat gold">
                  <div className="pm-score-stat-val">
                    {scoreAttempted > 0 ? Math.round((scoreCorrectCount / scoreAttempted) * 100) : 0}%
                  </div>
                  <div className="pm-score-stat-label">{t.scoreTotal}</div>
                </div>
              </div>

              <table className="pm-score-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t.scoreMod}</th>
                    <th>{t.scoreResult}</th>
                  </tr>
                </thead>
                <tbody>
                  {scoreRows.map((row, i) => {
                    const s = row.score;
                    return (
                      <tr key={i} style={{ cursor: "pointer" }} onClick={() => { goTo(i); setShowScore(false); }}>
                        <td style={{ color: "#6b7fa0", fontWeight: 700 }}>{row.id}</td>
                        <td>{lang === "en" ? row.titleEN : row.titleFR}</td>
                        <td>
                          {!s ? (
                            <span className="pm-score-badge pending">— {t.scoreNotDone}</span>
                          ) : s.correct ? (
                            <span className="pm-score-badge correct">✓ {t.scoreCorrect}</span>
                          ) : (
                            <span className="pm-score-badge wrong">✗ {t.scoreWrong}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {allDone && (
                <div style={{ marginTop: 20, textAlign: "center" }}>
                  <button className="pm-btn pm-btn-gold" onClick={() => { setShowScore(false); setShowCert(true); }}>
                    {t.certBtn}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── CERTIFICATE MODAL ── */}
      {showCert && (
        <div className="pm-cert-overlay" onClick={() => setShowCert(false)}>
          <div className="pm-cert-modal" onClick={e => e.stopPropagation()}>
            <div className="pm-cert-header">
              <h3>{t.certModal}</h3>
              <button className="pm-btn pm-btn-ghost pm-btn-sm" style={{ color: "#C9A84C" }} onClick={() => setShowCert(false)}>✕</button>
            </div>
            <div className="pm-cert-body">
              <div className="pm-cert-label">{t.certNameLabel}</div>
              <input
                className="pm-cert-input"
                placeholder={t.certNamePlaceholder}
                value={certName}
                onChange={e => setCertName(e.target.value)}
                maxLength={80}
              />

              <div style={{ marginTop: 16 }}>
                <div className="pm-cert-label">{t.certLang}</div>
                <div className="pm-cert-lang-toggle">
                  <button className={`pm-cert-lang-btn${certLang === "en" ? " active" : ""}`} onClick={() => setCertLang("en")}>
                    🇬🇧 English
                  </button>
                  <button className={`pm-cert-lang-btn${certLang === "fr" ? " active" : ""}`} onClick={() => setCertLang("fr")}>
                    🇫🇷 Français
                  </button>
                </div>
              </div>

              <div className="pm-cert-preview">
                <strong>{certLang === "en" ? "Certificate Preview" : "Aperçu du Certificat"}</strong><br/>
                {certLang === "en"
                  ? <>This certifies that <strong>{certName || "—"}</strong> has successfully completed all 10 modules of the <strong>Project Management Professional Certificate</strong>.<br/>Final Score: <strong>{totalCorrect} / 10 ({Math.round((totalCorrect / 10) * 100)}%)</strong></>
                  : <>Ceci certifie que <strong>{certName || "—"}</strong> a réussi l'ensemble des 10 modules du <strong>Certificat Professionnel en Gestion de Projet</strong>.<br/>Score Final : <strong>{totalCorrect} / 10 ({Math.round((totalCorrect / 10) * 100)} %)</strong></>
                }
              </div>

              <div className="pm-cert-actions">
                <button className="pm-btn pm-btn-outline" style={{ flex: 1 }} onClick={() => setShowCert(false)}>
                  {t.certClose}
                </button>
                <button
                  className="pm-btn pm-btn-gold"
                  style={{ flex: 2 }}
                  onClick={generateCertificate}
                  disabled={!jsPDFLoaded}
                >
                  {jsPDFLoaded ? t.certGenerate : (lang === "en" ? "Loading PDF library…" : "Chargement…")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
